import * as THREE from 'three';
import { BlockPhysics, type BoxCollider } from './collision';
import {
  BUILDINGS,
  BODEGA_DOOR,
  PARK_PROPS,
  SUBWAY_GATE,
  FLOORS,
  allWalls,
  boundaryWalls,
  FIRE_ESCAPE_RAMP,
  FIRE_ESCAPE_RAIL_SEGMENTS,
  FIRE_ESCAPE_LANDING_RAIL,
  ROOFTOP,
  ROOFTOP_LANDING,
  TUNNEL_RAMP,
  TUNNEL_FLOOR,
  TUNNEL_HEIGHT,
  SUBWAY_STAIR,
  SUBWAY_LANDING,
  STREET_HALF_WIDTH,
  SIDEWALK_WIDTH,
  CONSTRUCTION_SCAFFOLDS,
  SCAFFOLD_COLLIDERS,
  STREET_PROP_COLLIDERS,
  HIDDEN_STASH_MARKER,
  CORONA_EXIT_GATE,
  LAUNDROMAT_DOOR,
  BASEMENT_HATCH,
  ROOFTOP_B,
  ROOFTOP_D,
  ROOFTOP_D_RAMP,
  ROOFTOP_D_RAIL_SEGMENTS,
  ROOFTOP_D_LANDING,
  ROOFTOP_D_LANDING_RAIL,
  ROOFTOP_VANTAGE_TOWER_COLLIDER,
  EL_LINE_PIER_XS,
  EL_DECK_PLACEMENT_Y,
  EL_DECK_WALK_Y,
  EL_DECK_FLOOR,
  EL_STATION_STAIR,
  EL_STATION_SIDE_WALLS,
  EL_PIER_COLLIDERS,
  EL_DECK_RAILS,
  EL_TRAIN_POS,
  EL_TRAIN_PLACEMENT_Y,
  EL_TRAIN_COLLIDER,
  EL_TRAIN_NOSE_COLLIDER,
  EL_TRACK_STRIP_XS,
  EL_TRACK_STRIP_PLACEMENT_Y,
  EL_BUMPER_POS,
  EL_BUMPER_PLACEMENT_Y,
  EL_BUMPER_COLLIDER,
  EL_DECK_EAST_END_CAP,
  EL_STATION_BENCH_POS,
  EL_STATION_BENCH_COLLIDERS,
  EL_STATION_LAMP_POS,
  EL_STATION_LAMP_COLLIDERS,
  EL_STATION_TRASH_CAN_POS,
  EL_STATION_TRASH_CAN_COLLIDER,
  POOLHOUSE_POS,
  POOLHOUSE_PIER_COLLIDERS,
  LIFEGUARD_CHAIR_POS,
  LIFEGUARD_CHAIR_COLLIDER,
  FLAGPOLE_POS,
  FLAGPOLE_COLLIDER,
  BEER_GARDEN_POS,
  BEER_GARDEN_COLLIDERS,
  LONGTABLE_POS,
  LONGTABLE_COLLIDER,
  STRING_LIGHTS_POS,
  STRING_LIGHTS_COLLIDERS,
  TAVERNA_POS,
  TAVERNA_COLLIDERS,
  TAVERNA_FACADE_COLLIDER,
  SEAFOOD_POS,
  SEAFOOD_COLLIDERS,
  SEAFOOD_FACADE_COLLIDER,
  FISH_CRATE_POS,
  FISH_CRATE_COLLIDER,
  FISH_DISPLAY_POS,
  FISH_DISPLAY_COLLIDER,
  FAIR_STALL_POS,
  FAIR_STALL_COLLIDERS,
  GRILL_CART_POS,
  GRILL_CART_COLLIDER,
  RFK_TOWER_XS,
  RFK_SPAN_CENTER_X,
  RFK_VISTA_Z,
  RFK_TOWER_PLACEMENT_Y,
  RFK_SPAN_PLACEMENT_Y,
  BOMB_SIGN_POS,
} from './layout';
import {
  LAIR_ORIGIN,
  LAIR_FLOOR,
  LAIR_PILLARS,
  TUNNEL_BREACH_CENTER,
  TUNNEL_BREACH_PLACEMENT_Y,
  lairWalls,
  lairPillarColliders,
} from './denLair';
import {
  TRAP_HOLE_POS,
  TRAP_HOLE_PLACEMENT_Y,
  SEWER_ORIGIN,
  PIPE_ENTRY,
  PIPE_RUN_LENGTH,
  sewerWalls,
  sewerFloors,
} from './denSewer';
import { GRIDLOCK_WRECKS, GRIDLOCK_BARRIER_NORTH, GRIDLOCK_BARRIER_SOUTH, GRIDLOCK_BARRIER_HALF_LENGTH, GRIDLOCK_BARRIER_HALF_THICKNESS, GRIDLOCK_BARRIER_HEIGHT, GRIDLOCK_ENTRY_MOUTH, GRIDLOCK_ENTRY_MOUTH_HALF_WIDTH } from './arena';
import {
  INTERIOR_ORIGIN,
  INTERIOR_ROOM,
  INTERIOR_FLOOR,
  INTERIOR_COUNTER,
  INTERIOR_STOOP_FLOOR,
  HEAL_STATION,
  CRAFT_BENCH,
  HUNT_BOARD,
  interiorWalls,
  interiorCounterCollider,
  interiorCounterTopFloor,
  interiorStoopCurb,
} from './interior';
import {
  LAUNDROMAT_ROOM,
  LAUNDROMAT_FLOOR,
  LAUNDROMAT_STOOP_FLOOR,
  LAUNDROMAT_MACHINES,
  laundromatWalls,
  laundromatStoopCurb,
  laundromatMachinesCollider,
  laundromatMachinesTopFloor,
} from './interiorLaundromat';
import { BASEMENT_ROOM, BASEMENT_FLOOR, BASEMENT_SEALED_DOOR_MARKER, basementWalls } from './interiorBasement';
import { loadGLB, instantiate, type LoadedGLB } from '../core/assets';
import { applyWaterMaterial } from './waterMaterial';

/** L3 (PLAN-CITY3 §0): every permanent gray ground primitive is a real box, never a zero-thickness plane — see `addFlatMesh`/`addRampMesh`'s own comments for the exact construction. 0.15m is L3's stated minimum. */
const GROUND_THICKNESS = 0.15;

/**
 * Builds the S01 gray Astoria block from `layout.ts`'s data — every named
 * feature from PLAN-CITY's S01 row (intersection, 4 buildings, alley,
 * rooftop + fire-escape ramp, subway stair, park pocket, utility tunnel,
 * bodega door) as primitive meshes, per §0.4 ("gray-box geometry is code,
 * not art"). Replaces S00's single gray plane. Exposes `physics`
 * (BlockPhysics) for the controller and `raycastGround` for the dev
 * teleport tool (S00's teleport lands on whatever's under the tap, now
 * multi-level instead of one flat plane). S05 added the play-area boundary
 * ring (`addBoundaryWalls`) and the bodega interior (`addInterior`,
 * world/interior.ts) — both merged into this SAME `physics` instance.
 */
export class Block {
  readonly group = new THREE.Group();
  readonly physics: BlockPhysics;
  private readonly groundMeshes: THREE.Object3D[] = [];
  /**
   * T3-1: everything belonging to the bodega interior (gray-box room +
   * stoop, home-base props, and — once loaded — the real `attachInterior()`
   * art) lives under this ONE group so `setInteriorVisible()` can hide the
   * whole thing with a single flag flip — cheapest correct fix per
   * PLAN-CITY2 §2 ("moving the room is riskier and buys nothing"). Starts
   * hidden; main.ts syncs the real value from `insideBodega` the moment
   * that's known (mirrors S11's own "sync before the first orbitCam.snapTo()"
   * discipline). Purely visual — `physics` (collision) is untouched by this
   * flag, since the room is only ever reachable via teleport regardless of
   * whether it's currently rendered.
   *
   * T3-1 review finding (LOW, don't-break-list lens, CONFIRMED latent, not
   * live): `interiorGroup` is one entry of `this.group.children`, which
   * main.ts passes as `orbitCam`'s own `occluders` list — three.js's
   * Raycaster never checks `.visible` (confirmed against the library
   * source), so the OUTDOOR camera's occlusion raycast can still geometrically
   * "see" this group even while hidden. Harmless today only because
   * `INTERIOR_ORIGIN` (x=140) sits ~100m past the real play boundary (±38)
   * and every outdoor `CAMERA_ZOOM_DISTANCES` preset caps the raycast at
   * ≤7.5m (core/camera.ts) — geometrically unreachable. NOT fixable by
   * excluding `interiorGroup` from `occluders` outright: while
   * `insideBodega` IS true, this same group is exactly what the INDOOR
   * camera clamp needs to occlude against. If a future session moves
   * `INTERIOR_ORIGIN` closer to the block, or meaningfully raises the `far`
   * zoom preset, re-check this margin.
   */
  private readonly interiorGroup = new THREE.Group();
  /**
   * B-2 "Enterable interiors" (PLAN-CITY2 §3) — same one-group-per-room
   * visibility idiom as `interiorGroup` above, one per new room. Kept as
   * separate groups/methods rather than generalizing `interiorGroup` into a
   * keyed collection: `interiorGroup`'s own machinery (this class's
   * `isEffectivelyVisible`/`raycastGround` filter, main.ts's
   * `activeCameraMaxDistance`/self-healing-invariant callers) has already
   * been through three review rounds (T3-1 + two follow-ups) — mirroring its
   * exact shape for two more instances is lower-risk than refactoring it
   * into a generic multi-room engine this session would be the first to run
   * through review.
   */
  private readonly laundromatGroup = new THREE.Group();
  private readonly basementGroup = new THREE.Group();
  /**
   * Wave D (PLAN-CITY2 §4) — the two den interiors, same one-group-per-room
   * visibility idiom as `laundromatGroup`/`basementGroup` above. Unlike
   * those two (still S05-era gray boxes with a LATER real-art swap), the
   * C07 kit ships REAL geometry for both rooms from the start — no gray
   * phase, mirroring how B-3's `attachBeerGarden()` etc. place real art
   * directly with no placeholder predecessor.
   */
  private readonly lairGroup = new THREE.Group();
  private readonly sewerGroup = new THREE.Group();
  /** Populated once `attachDenLair()` resolves — main.ts reads this (via `getLairPillarGroups()`) to wire each pillar's intact/cracked mesh swap into `world/dens.ts`'s `LairEncounter`. */
  private readonly lairPillarInstances: THREE.Object3D[] = [];
  /**
   * S09: the gray-box placeholder meshes, keyed by feature — populated in
   * the constructor, consumed (and deleted) one key at a time by `attachArt`
   * once that feature's real C04 art has actually loaded. Never touched by
   * `physics` (§0.4: visuals and collision are fully independent here, see
   * this class's own header comment) — swapping/removing a key only ever
   * mutates `group`/`groundMeshes`, never a `BoxCollider`/`FloorRegion`.
   */
  private readonly gray: Record<string, THREE.Object3D | THREE.Object3D[]> = {};
  /**
   * S09: kicked off automatically at construction (fire-and-forget, same
   * "tolerate the gap" async pattern main.ts already uses for enm_rat.glb/
   * bos_gridlock.glb — see core/assets.ts) — every gray-box feature stays
   * visible and fully functional until its own real art resolves, so a slow
   * or failed load degrades to "still looks like S01," never to a hole in
   * the world. Exposed so a harness check (or a future caller) can await
   * "is the real C04 art definitely in" instead of guessing a timeout.
   */
  readonly artReady: Promise<void>;

  constructor() {
    // S05: the SAME single BlockPhysics instance also carries the boundary
    // ring and the (spatially isolated, world/interior.ts's own header
    // comment explains why) bodega interior — every other system that holds
    // a `physics`/`block.physics` reference (PlayerCapsule, EnemyManager,
    // NavGrid) keeps working unchanged, since they all just query points,
    // never enumerate the collider lists themselves.
    this.physics = new BlockPhysics(
      // B-2: LAUNDROMAT_FLOOR/laundromatMachinesTopFloor()/LAUNDROMAT_STOOP_FLOOR
      // and BASEMENT_FLOOR merge into this SAME instance, same "one physics
      // object, every system just queries points" reasoning the bodega's own
      // merge already established (this class's own header comment).
      [
        ...FLOORS,
        INTERIOR_FLOOR,
        interiorCounterTopFloor(),
        INTERIOR_STOOP_FLOOR,
        LAUNDROMAT_FLOOR,
        laundromatMachinesTopFloor(),
        LAUNDROMAT_STOOP_FLOOR,
        BASEMENT_FLOOR,
        // B-3: the elevated-line deck top + station stair — see layout.ts's
        // own EL_DECK_FLOOR/EL_STATION_STAIR comments for the measured
        // walk-height derivation.
        EL_DECK_FLOOR,
        EL_STATION_STAIR,
        // Wave D-1/D-3: the two den interiors (isolated origins, same "one
        // physics instance, every system just queries points" merge every
        // prior room already used).
        LAIR_FLOOR,
        ...sewerFloors(),
        // Wave D-2: each wreck's walkable deck — the FOOTPRINT LAW (see
        // world/arena.ts's own GRIDLOCK_WRECKS comment): this floor region's
        // XZ is IDENTICAL to that same wreck's own body-blocker collider
        // below, never the narrower visual deck mesh, so `findVaultLedge`
        // can never land the player on a body-top spot with no registered
        // floor underneath (world/layout.ts's `PARK_PROP_TOPS` precedent).
        ...GRIDLOCK_WRECKS.map((w) => ({
          kind: 'flat' as const,
          minX: w.x - w.halfWidth,
          maxX: w.x + w.halfWidth,
          minZ: w.z - w.halfDepth,
          maxZ: w.z + w.halfDepth,
          y: w.deckY,
        })),
      ],
      // T3-3 (§0.3): STREET_PROP_COLLIDERS/SCAFFOLD_COLLIDERS give the
      // already-real (no gray-box phase ever existed for these — see their
      // own attachX() header comments) C04 street dressing and construction
      // scaffolds real collision for the first time — purely additive to the
      // wall list, no visual change (real art already renders these; this
      // just backs it with a matching invisible collider, same as any other
      // real-art wall in this file).
      // T3-1 review fix (MEDIUM): interiorStoopCurb() closes the stoop's own
      // open south edge — see its own comment (interior.ts) for why this is
      // an invisible collider, not a visual mismatch risk.
      [
        ...allWalls(),
        ...boundaryWalls(),
        ...interiorWalls(),
        interiorCounterCollider(),
        interiorStoopCurb(),
        ...STREET_PROP_COLLIDERS,
        ...SCAFFOLD_COLLIDERS,
        // B-2: laundromat (a walk-through doorway, same stoop-curb pattern
        // as the bodega above) + basement (fully solid, no doorway gap — see
        // interiorBasement.ts's own header comment for why).
        ...laundromatWalls(),
        laundromatMachinesCollider(),
        laundromatStoopCurb(),
        ...basementWalls(),
        // B-3: elevated-line piers/station side-walls/deck-edge rails +
        // Astoria Park poolhouse piers/lifeguard chair/flagpole + beer
        // garden walls/longtable + restaurant-row storefront posts/ice
        // display/fish crate/fish display + fair-stall counter+rearwall/
        // grill cart. Every box measured live off its own delivered GLB's
        // COL_ node transforms (dump-col-nodes probe) per layout.ts's own
        // comments — none guessed.
        ...EL_PIER_COLLIDERS,
        ...EL_STATION_SIDE_WALLS,
        ...EL_DECK_RAILS,
        // Wave E (PLAN-CITY3 §6): the train's mandatory nose collider (west
        // end-cap, discharged by the train's own body + this second seal)
        // and the east end-cap rail (E-1's own deliverable) — see
        // layout.ts's own EL_TRAIN_NOSE_COLLIDER/EL_DECK_EAST_END_CAP
        // comments. Platform-content colliders (benches/lamp/trash-can)
        // translate the same measured per-unit box every other shipped
        // instance of these props already uses, re-based onto deck height.
        EL_TRAIN_COLLIDER,
        EL_TRAIN_NOSE_COLLIDER,
        EL_BUMPER_COLLIDER,
        EL_DECK_EAST_END_CAP,
        ...EL_STATION_BENCH_COLLIDERS,
        ...EL_STATION_LAMP_COLLIDERS,
        EL_STATION_TRASH_CAN_COLLIDER,
        ...POOLHOUSE_PIER_COLLIDERS,
        LIFEGUARD_CHAIR_COLLIDER,
        FLAGPOLE_COLLIDER,
        ...BEER_GARDEN_COLLIDERS,
        LONGTABLE_COLLIDER,
        ...STRING_LIGHTS_COLLIDERS,
        ...TAVERNA_COLLIDERS,
        TAVERNA_FACADE_COLLIDER, // Wave P-4 (L4 corollary): the facade wall itself, walk-through until now
        ...SEAFOOD_COLLIDERS,
        SEAFOOD_FACADE_COLLIDER,
        FISH_CRATE_COLLIDER,
        FISH_DISPLAY_COLLIDER,
        ...FAIR_STALL_COLLIDERS,
        GRILL_CART_COLLIDER,
        // Wave D-1: lair walls (real east-doorway gap) + pillar bodies (6.0m
        // tall — genuinely solid, never vaultable, findVaultLedge rejects
        // anything above VAULT_MAX_HEIGHT automatically).
        ...lairWalls(),
        ...lairPillarColliders(),
        // Wave D-3: sewer chamber walls (real south pipe-mouth gap) + the
        // pipe bore's own side walls, both from world/denSewer.ts.
        ...sewerWalls(),
        // Wave D-2: each wreck's own body-blocker — SAME XZ footprint as its
        // matching floor region above (the FOOTPRINT LAW), topY at deck
        // height so the deck itself reads as a real ledge to vault onto.
        ...GRIDLOCK_WRECKS.map((w) => ({
          minX: w.x - w.halfWidth,
          maxX: w.x + w.halfWidth,
          minZ: w.z - w.halfDepth,
          maxZ: w.z + w.halfDepth,
          bottomY: 0,
          topY: w.deckY,
        })),
        // Wave D-2: the barrier line — genuinely blocks (height exceeds both
        // VAULT_MAX_HEIGHT and the discrete jump apex), never a floor region.
        ...[...GRIDLOCK_BARRIER_NORTH, ...GRIDLOCK_BARRIER_SOUTH].map((s) => ({
          minX: s.x - GRIDLOCK_BARRIER_HALF_LENGTH,
          maxX: s.x + GRIDLOCK_BARRIER_HALF_LENGTH,
          minZ: s.z - GRIDLOCK_BARRIER_HALF_THICKNESS,
          maxZ: s.z + GRIDLOCK_BARRIER_HALF_THICKNESS,
          bottomY: 0,
          topY: GRIDLOCK_BARRIER_HEIGHT,
        })),
      ],
    );

    this.interiorGroup.visible = false;
    this.laundromatGroup.visible = false;
    this.basementGroup.visible = false;
    this.lairGroup.visible = false;
    this.sewerGroup.visible = false;
    this.group.add(this.interiorGroup, this.laundromatGroup, this.basementGroup, this.lairGroup, this.sewerGroup);

    const [street, sidewalk] = this.addStreetAndSidewalks();
    this.gray.street = street;
    this.gray.sidewalk = sidewalk;
    // S09 review fix: one key per building/prop (not one shared array key)
    // — `attachBuildingFacades`/`attachParkProps` resolve each item's own
    // load independently, and a shared key gated on the WHOLE batch
    // succeeding left every OTHER already-loaded sibling stuck permanently
    // double-rendered (real art + gray box both visible) the instant any
    // ONE item in the batch failed to load. Per-item keys mean a single
    // failure only ever strands its OWN gray box, exactly matching this
    // class's own stated "every gray-box feature stays visible until its
    // own real art resolves" design (this class's header comment).
    for (const b of BUILDINGS) this.gray[`building_${b.id}`] = this.addBuilding(b);
    this.gray.bodegaDoor = this.addBox(BODEGA_DOOR.x, BODEGA_DOOR.height / 2, BODEGA_DOOR.z, BODEGA_DOOR.width, BODEGA_DOOR.height, BODEGA_DOOR.depth, BODEGA_DOOR.color, false);
    // Wave P-4 (PLAN-CITY3 §3): this used to be a permanent, untracked
    // primitive ("no delivered art exists for it") — the Greek Market
    // facade (building_b) actually ships a real modeled `closed_glass_door`
    // node that LAUNDROMAT_DOOR's x/width now measure exactly (see that
    // const's own comment), so this blue placeholder slab is now tracked
    // and removed once building_b's facade loads (`attachBuildingFacades`'s
    // building_b branch below) — same "gray box until real art exists"
    // idiom as every other tracked prop in this file, not a permanent one.
    this.gray.laundromatDoor = this.addBox(LAUNDROMAT_DOOR.x, LAUNDROMAT_DOOR.height / 2, LAUNDROMAT_DOOR.z, LAUNDROMAT_DOOR.width, LAUNDROMAT_DOOR.height, LAUNDROMAT_DOOR.depth, LAUNDROMAT_DOOR.color, false);
    for (const p of PARK_PROPS) this.gray[`park_${p.id}`] = this.addBox(p.x, p.height / 2, p.z, p.width, p.height, p.depth, p.color, true);
    this.gray.subwayGate = this.addBox(
      SUBWAY_GATE.x,
      SUBWAY_LANDING.y + SUBWAY_GATE.height / 2,
      SUBWAY_GATE.z,
      SUBWAY_GATE.width,
      SUBWAY_GATE.height,
      SUBWAY_GATE.depth,
      SUBWAY_GATE.color,
      false,
    );

    this.gray.fireEscapeRamp = this.addRampMesh(FIRE_ESCAPE_RAMP, 'z', 0x707686);
    this.gray.fireEscapeRail = this.addFireEscapeRail();
    this.gray.rooftop = this.addFlatMesh(ROOFTOP, 0x76798a, true);
    this.gray.rooftopLanding = this.addFlatMesh(ROOFTOP_LANDING, 0x76798a, true);
    // B-1 "Rooftop network" (PLAN-CITY2 §3): the real C04 facade art for
    // EVERY building already ships its own `roof_deck`+`parapet_*` nodes
    // (confirmed via a live world-space bbox probe this session; only
    // building_a's was ever wired up until `attachBuildingFacades()` below).
    // Review finding (CONFIRMED, don't-break-list): building_a's OWN gray
    // `rooftop` mesh stays registered in `groundMeshes` as a real fallback
    // until its facade genuinely succeeds (`removeGray('bodegaDoor',
    // 'rooftop')` only runs inside the try's success path, per that
    // function's own header comment) — building_b/building_d had no
    // equivalent gray deck at all, so a facade LOAD FAILURE for either
    // (the existing per-building catch block already anticipates exactly
    // this) would leave `FLOORS` still reporting a walkable ROOFTOP_B/
    // ROOFTOP_D (pure data, load-independent) but nothing in `groundMeshes`
    // there for the dev-tap teleport to target. Fixed the same way
    // building_a's own rooftop already is: a tracked gray deck per building,
    // removed only on that SPECIFIC building's own successful load.
    this.gray.rooftopB = this.addFlatMesh(ROOFTOP_B, 0x76798a, true);
    this.gray.rooftopD = this.addFlatMesh(ROOFTOP_D, 0x76798a, true);
    // Wave P-2 (PLAN-CITY3 §3): the second climbing ramp to Building D's
    // real roof used to have no delivered art at all — CITY3-FINDINGS.md's
    // own "CHEAP" fix, now built below in `attachRooftopDFireEscape()`,
    // reskins it with the already-shipped `blk_fire_escape.glb` via
    // `attachFireEscape()`'s exact tiling idiom (3 flights of its 4m-rise/
    // 4m-run module exactly cover this ramp's 12m/12m run). Tracked as gray
    // keys now (they weren't before) so that method can remove them once
    // its own real art lands — same "permanent gray-box until real art
    // exists" idiom every other tracked surface in this file already uses.
    // The small vantage tower stays a permanent gray box (§3's own "still-
    // gray, needs dressing or C12 art" list — out of P-2's scope).
    this.gray.rooftopDRamp = this.addRampMesh(ROOFTOP_D_RAMP, 'z', 0x707686);
    this.gray.rooftopDRail = this.addRooftopDRail();
    this.gray.rooftopDLanding = this.addFlatMesh(ROOFTOP_D_LANDING, 0x76798a, true);
    this.addBoxCollider(ROOFTOP_VANTAGE_TOWER_COLLIDER, 0x8a8f9c, true);
    this.gray.tunnelRamp = this.addRampMesh(TUNNEL_RAMP, 'z', 0x55565f);
    this.gray.tunnelFloor = this.addFlatMesh(TUNNEL_FLOOR, 0x3f4048, true);
    this.gray.tunnelWalls = this.addTunnelWalls();
    this.gray.subwayStair = this.addRampMesh(SUBWAY_STAIR, 'z', 0x55565f);
    this.gray.subwayLanding = this.addFlatMesh(SUBWAY_LANDING, 0x3f4048, true);
    this.addSubwayWalls(); // C04 has no side-wall art for the descent — stays gray, not tracked for removal (S10/a future polish pass)
    this.gray.subwayRimCurb = this.addSubwayRimCurb();

    this.addBoundaryWalls();
    const interiorMeshes = this.addInterior();
    this.gray.interior = interiorMeshes;
    // T3-1: the stoop is a NEW feature with no equivalent in the delivered
    // `blk_bodega_interior.glb` (attachInterior() below) — kept OUT of
    // `this.gray.interior` so `removeGray('interior')` never deletes it once
    // real interior art loads, matching `addBodegaHomeBaseProps()`'s own
    // "permanent, not tracked in `this.gray`" precedent two methods down.
    const stoop = this.addInteriorStoop();
    this.interiorGroup.add(...interiorMeshes, stoop);

    // S10: primitive-only additions (§0.4 — no delivered Codex art exists
    // for any of these) — permanent, not tracked in `this.gray` since
    // there's no future real-art swap to remove them for. `blk_scaffold`
    // (a real delivered asset) is the one S10 piece that DOES go through
    // the async attachArt() pipeline below, alongside every other real C04
    // piece.
    this.addBox(CORONA_EXIT_GATE.x, CORONA_EXIT_GATE.height / 2, CORONA_EXIT_GATE.z, CORONA_EXIT_GATE.width, CORONA_EXIT_GATE.height, CORONA_EXIT_GATE.depth, CORONA_EXIT_GATE.color, false);
    this.addCoronaExitSign();
    this.addHiddenStashCrate();
    // T3-1: home-base props (heal/craft/hunt board) are bodega-interior
    // dressing — reparented into `interiorGroup` alongside the room itself,
    // same visibility toggle, not left as always-visible direct children of
    // `this.group` the way `addHiddenStashCrate()`'s EXTERIOR tunnel prop
    // correctly stays.
    this.interiorGroup.add(...this.addBodegaHomeBaseProps());

    // B-2 "Enterable interiors" (PLAN-CITY2 §3) — gray-box only, no delivered
    // Codex art exists for either room, so (unlike the bodega above) there's
    // no `attachArt()` entry to add for them, matching the CORONA_EXIT_GATE
    // sign / hidden-stash-crate precedent two lines up: permanent primitives,
    // never tracked in `this.gray` since there's no future real-art swap.
    this.laundromatGroup.add(...this.addLaundromat());
    this.basementGroup.add(...this.addBasement());
    this.addBasementHatchGrate(); // exterior tunnel marker — always visible, like HIDDEN_STASH_MARKER's own crate above

    this.artReady = this.attachArt();
  }

  /** A small placard above CORONA_EXIT_GATE — purely visual, no collider of its own (the gate box below it already blocks the street). */
  private addCoronaExitSign(): void {
    this.addBox(CORONA_EXIT_GATE.x, CORONA_EXIT_GATE.height + 0.3, CORONA_EXIT_GATE.z, 0.15, 0.6, 1.8, 0xf2c14e, false);
  }

  /**
   * A small gray-box crate marking the hidden stash (world/interact.ts's
   * `hidden_stash` action) — §0.4 primitive, no delivered prop asset exists
   * for this yet. Deliberately collision-free — see
   * `addBodegaHomeBaseProps()`'s own comment for why an interact-marker box
   * in this file skips a collider rather than risk the vault-oscillation bug
   * class a short solid prop needs a matching top-floor region to avoid.
   *
   * Wave P-4 (PLAN-CITY3 §3, CITY3-FINDINGS.md's "3.35m mid-air" finding):
   * `cy` was a bare street-level literal (0.35) even though this crate sits
   * on TUNNEL_FLOOR (y=-3), not street level (y=0) — the crate rendered
   * 3.35m above its own floor. Fixed to be TUNNEL_FLOOR-relative, the same
   * "derive from the real floor, never a bare literal" discipline every
   * other tunnel-floor prop in this file already follows.
   */
  private addHiddenStashCrate(): void {
    this.addBox(HIDDEN_STASH_MARKER.x, TUNNEL_FLOOR.y + 0.35, HIDDEN_STASH_MARKER.z, 0.7, 0.7, 0.7, 0x6b5a3a, false);
  }

  /**
   * S10 bodega home base v1 (extended S11 with HUNT_BOARD): small gray-box
   * anchors for HEAL/CRAFT/HUNT BOARD (§0.4 — none has delivered art yet).
   * STASH reuses the existing counter prop (already placed by
   * `addInterior()`/`attachInterior()`), so it needs no visual of its own
   * here.
   *
   * Deliberately collision-free (`isGround=false`, and — unlike every
   * VAULTABLE short prop in this file, PARK_PROPS/INTERIOR_COUNTER — no
   * matching `BoxCollider`/top-floor-region pair either): these are
   * interact-trigger markers, not obstacles, the same "pure dressing, zero
   * collision" idiom `attachStreetProps()`'s own header comment already
   * establishes in this file for props with no gameplay-blocking role.
   * Deliberately NOT given a solid collider under time pressure either —
   * a short prop's collider is only safe once it ALSO has the matching
   * top-floor region PARK_PROP_TOPS/interiorCounterTopFloor() add (its own
   * comment explains why: without it, vaulting onto the prop free-falls
   * back through it and re-triggers the vault every frame, a real,
   * previously-hit bug class in this project) — adding one without the
   * other here would trade a cosmetic walk-through for a functional
   * oscillation bug, a worse trade.
   */
  private addBodegaHomeBaseProps(): THREE.Mesh[] {
    const heal = this.addBox(HEAL_STATION.x, 0.5, HEAL_STATION.z, 0.5, 1, 0.4, 0xd94f4f, false);
    // B-3: tracked in `this.gray` (unlike craft/board below) so
    // attachTheBomb() can swap it for the real prp_the_bomb.glb pickup once
    // that loads — safe even though `heal` is parented to `interiorGroup`,
    // not `this.group` directly, since `removeGray()` uses `obj.parent`, not
    // a hardcoded parent.
    this.gray.healMarker = heal;
    // Review finding (CONFIRMED): at width 0.9, this box's east face
    // (142.7+0.45=143.15) sat 5cm inside the room's real east wall
    // (interiorWalls(), x∈[143.1,143.5]) — shrunk to 0.6 (east face 143.0,
    // 0.1m real clearance) rather than moving CRAFT_BENCH's own marker
    // position, since the interact trigger (world/interact.ts) keys off
    // that marker directly and didn't need to move.
    const craft = this.addBox(CRAFT_BENCH.x, 0.45, CRAFT_BENCH.z, 0.6, 0.9, 0.6, 0x7a6a52, false);
    // S11: a thin wall-mounted board (shallow 0.15m depth, unlike the two
    // free-standing props above) — deliberately doesn't read as an
    // obstacle to walk around in this already-tight room, matching the
    // real design intent (a corkboard/sign, not a piece of furniture).
    const board = this.addBox(HUNT_BOARD.x, 0.6, HUNT_BOARD.z, 0.6, 1.2, 0.15, 0x3a5a8a, false);
    return [heal, craft, board];
  }

  /** T3-1: main.ts calls this everywhere `insideBodega` is set (enter/exit, the S07 resume path, and once at boot right before the first `orbitCam.snapTo()`) — see `interiorGroup`'s own field comment for why one flag is enough. */
  setInteriorVisible(visible: boolean): void {
    this.interiorGroup.visible = visible;
  }

  /** T3-1: exposed for the harness (same "expose the raw thing for direct testing" convention as e.g. main.ts's resolveAimDirectionRaw) — asserting the interior is genuinely hidden outdoors, not just inferring it from indirect symptoms. */
  isInteriorVisible(): boolean {
    return this.interiorGroup.visible;
  }

  /** B-2: mirrors `setInteriorVisible`/`isInteriorVisible` exactly, one pair per new room (see `laundromatGroup`'s own field comment for why these are separate methods, not a generalized keyed API). */
  setLaundromatVisible(visible: boolean): void {
    this.laundromatGroup.visible = visible;
  }

  isLaundromatVisible(): boolean {
    return this.laundromatGroup.visible;
  }

  setBasementVisible(visible: boolean): void {
    this.basementGroup.visible = visible;
  }

  isBasementVisible(): boolean {
    return this.basementGroup.visible;
  }

  /** Wave D-1: mirrors `setLaundromatVisible`/`isLaundromatVisible` exactly. */
  setLairVisible(visible: boolean): void {
    this.lairGroup.visible = visible;
  }

  isLairVisible(): boolean {
    return this.lairGroup.visible;
  }

  /** Wave D-3: mirrors `setLaundromatVisible`/`isLaundromatVisible` exactly. */
  setSewerVisible(visible: boolean): void {
    this.sewerGroup.visible = visible;
  }

  isSewerVisible(): boolean {
    return this.sewerGroup.visible;
  }

  /** Wave D-1: the 6 pillar GLB instances, in `LAIR_PILLARS` index order — main.ts feeds each into `LairEncounter.registerPillarGroup()` once `artReady` resolves, so the RED CHARGE intact/cracked mesh swap has something to toggle. Empty until `attachDenLair()` finishes. */
  getLairPillarGroups(): readonly THREE.Object3D[] {
    return this.lairPillarInstances;
  }

  /** T3-1 review fix (LOW, correctness lens): true only if `obj` AND every ancestor up to (and including) `this.group` is visible — three.js's own Raycaster never checks `.visible` at any level (confirmed against the library source), so `interiorGroup.visible=false` alone does NOT stop `intersectObjects` from hitting the hidden interior's floor/stoop meshes, which stay in `groundMeshes` unconditionally regardless of that flag. */
  private isEffectivelyVisible(obj: THREE.Object3D): boolean {
    let o: THREE.Object3D | null = obj;
    while (o) {
      if (!o.visible) return false;
      if (o === this.group) return true;
      o = o.parent;
    }
    return true;
  }

  raycastGround(raycaster: THREE.Raycaster): THREE.Vector3 | null {
    // S09: recursive=true (was false) — every S00-S08 entry here was always
    // a single leaf Mesh, so this is a no-op for all of them (a leaf has no
    // children to recurse into); it's what lets a loaded GLB GROUP (a
    // facade, a park prop) be pushed here directly and still raycast
    // correctly against whatever real mesh is inside it, with no per-asset
    // named-node lookup required.
    // T3-1 review fix: filtered to effectively-visible candidates only —
    // without this, the devMode-only tap-teleport could land the player at
    // the hidden bodega interior (INTERIOR_ORIGIN) whenever insideBodega is
    // false, reproducing an invisible-floor variant of the exact bug T3-1
    // fixes, just for the dev tool rather than normal play.
    const visibleGroundMeshes = this.groundMeshes.filter((m) => this.isEffectivelyVisible(m));
    const hits = raycaster.intersectObjects(visibleGroundMeshes, true);
    return hits.length > 0 ? hits[0].point : null;
  }

  /**
   * S12 gate-pass fix: straight-down raycast at a given (x,z), from `fromY`.
   * Exists so the harness can confirm `buildHoledGroundShape`'s cut holes are
   * real gaps in the rendered MESH, not just in `BlockPhysics`'s separate
   * collider list — until now every fall-through check exercised physics
   * only (T1 review note: "a broken street MESH would pass 41/41"). Null
   * means nothing in `groundMeshes` was hit at this column — either a real
   * cut hole, or a bug; callers must distinguish those against a known-solid
   * control point, same as any other negative-result assertion.
   */
  raycastGroundDownAt(x: number, z: number, fromY = 50): THREE.Vector3 | null {
    return this.raycastGround(new THREE.Raycaster(new THREE.Vector3(x, fromY, z), new THREE.Vector3(0, -1, 0)));
  }

  /**
   * S09: removes one or more gray-box placeholders by their `this.gray` key
   * — pulled out of ITS OWN CURRENT PARENT (so it stops rendering) and, if
   * it was ever pushed to `groundMeshes` (dev-tap teleport), pulled out of
   * there too, so a stale reference never shadows the real art now standing
   * in its place. Safe to call with a key that was never populated or
   * already removed (a no-op) — every `attachX` method calls this
   * unconditionally once its own art has loaded, regardless of whether a
   * sibling `attachY` got there first on a shared key.
   *
   * T3-1 fix: was `this.group.remove(obj)` — every S09-era gray key's own
   * objects really were direct children of `this.group`, so that worked,
   * but the interior's own gray meshes are now reparented into
   * `interiorGroup` (T3-1's own visibility-toggle fix) and `Object3D.remove`
   * silently no-ops on an object that isn't actually ITS child (confirmed
   * against the three.js source: it only splices `this.children` after an
   * `indexOf` hit) — `this.gray.interior`'s meshes would otherwise never
   * actually leave the scene once real interior art loads, permanently
   * double-rendering gray-box UNDER the real art forever. `obj.parent`
   * always points at wherever the object ACTUALLY lives right now, so this
   * is correct for every existing key too (all of which really are parented
   * to `this.group`), not just the interior's.
   */
  private removeGray(...keys: string[]): void {
    for (const key of keys) {
      const value = this.gray[key];
      if (!value) continue;
      const objects = Array.isArray(value) ? value : [value];
      for (const obj of objects) {
        obj.parent?.remove(obj);
        const gi = this.groundMeshes.indexOf(obj);
        if (gi >= 0) this.groundMeshes.splice(gi, 1);
      }
      delete this.gray[key];
    }
  }

  /**
   * T1 3a: the street used to be a single opaque 80×80 plane at y=0 covering
   * BOTH the tunnel and subway pits — physically you descend into them (real
   * floor regions below street level, per collision.ts), but visually you
   * sink through solid-looking ground into darkness (the "fell through the
   * floor" report, reproduced at the exact spot walking north into the
   * tunnel mouth at x=18). Cut real holes over each pit's footprint via a
   * THREE.Shape (the 80×80 outline) carrying two rectangular holes, instead
   * of one solid plane — `raycastGround` still works unchanged (this mesh
   * stays in `groundMeshes`; the ramp/landing meshes under each hole are
   * already registered separately, so a tap into an opening still resolves
   * to something).
   */
  private addStreetAndSidewalks(): [THREE.Mesh, THREE.Mesh] {
    // ShapeGeometry lies in the local XY plane; after this mesh's own
    // `rotation.x = -Math.PI/2` (same rotation the old flat street plane
    // used), local (x,y) maps to world (x, -y) exactly like S01's own
    // addFlatMesh/addRampMesh planes do (`mesh.rotation.x=-Math.PI/2`
    // consistently across this file) — so a hole meant to land at world Z
    // range [z0,z1] must be authored at local Y range [-z1,-z0] (negated
    // AND order-swapped). Local X needs no conversion (maps straight to
    // world X). Verified against this exact block's own tunnel/subway
    // footprints (signed-area + point-in-hole probes across the block, by
    // hand during T1) — NOT covered by any automated harness check. The
    // tunnelDescent/subwayRimTest checks exercise BlockPhysics (layout.ts
    // data) only; nothing in the harness reads this MESH, so a wrong hole
    // here (misplaced, missing, or the whole street culled) would pass the
    // full harness while looking broken to a player. If this function is
    // ever edited, re-verify visually or add a real mesh-raycast check.
    // DoubleSide, matching `addFlatMesh`'s own flat-ground convention in
    // this file (ROOFTOP/TUNNEL_FLOOR/SUBWAY_LANDING all use it) — a
    // ShapeGeometry's winding direction is easy to get backward by hand
    // (unlike a plain PlaneGeometry, there's no library default to lean
    // on), and getting it wrong would silently back-face-cull the entire
    // street from every camera angle above it. DoubleSide makes that class
    // of mistake harmless instead of a severe, easy-to-miss regression.
    const streetMat = new THREE.MeshStandardMaterial({ color: 0x3a3b40, roughness: 0.95, side: THREE.DoubleSide });
    const street = new THREE.Mesh(this.buildHoledGroundShape(80, 80, 0, 0), streetMat);
    street.rotation.x = -Math.PI / 2;
    street.position.y = 0;
    this.group.add(street);
    this.groundMeshes.push(street);

    // Sidewalks as a lighter strip along the outer edge of the street cross —
    // purely visual (walking is one continuous flat street floor region), a
    // gray-box cue for where the road ends, per §0.4. Not a groundMesh (the
    // dev-tap raycast was never meant to land on it specifically), but it's
    // still OPAQUE geometry that overlapped the subway pit's west edge
    // (this strip spans x −6.7..6.7; the pit is x −8..−3) before this fix —
    // same hole treatment, clipped to this mesh's own (narrower) bounds.
    const sidewalkMat = new THREE.MeshStandardMaterial({ color: 0x9a9a9e, roughness: 0.9, side: THREE.DoubleSide });
    const sidewalkWidth = STREET_HALF_WIDTH * 2 + SIDEWALK_WIDTH * 2 + 0.4;
    const ns = new THREE.Mesh(this.buildHoledGroundShape(sidewalkWidth, 80, 0, 0), sidewalkMat);
    ns.rotation.x = -Math.PI / 2;
    ns.position.y = 0.005;
    this.group.add(ns);

    return [street, ns];
  }

  /**
   * An axis-aligned `width`×`depth` rectangle (centered at `cx`,`cz` in
   * world XZ) with the tunnel and subway pit footprints cut out of it,
   * clipped to whatever part of each hole actually falls inside this
   * rectangle's own bounds (a hole that doesn't overlap this mesh's extent
   * at all is skipped entirely — `THREE.ShapeGeometry` doesn't tolerate a
   * hole path outside its outer contour).
   */
  private buildHoledGroundShape(width: number, depth: number, cx: number, cz: number): THREE.ShapeGeometry {
    const minX = cx - width / 2;
    const maxX = cx + width / 2;
    const minZ = cz - depth / 2;
    const maxZ = cz + depth / 2;

    const shape = new THREE.Shape();
    shape.moveTo(minX, -minZ);
    shape.lineTo(maxX, -minZ);
    shape.lineTo(maxX, -maxZ);
    shape.lineTo(minX, -maxZ);
    shape.lineTo(minX, -minZ);

    const tunnelHole = { minX: TUNNEL_RAMP.minX, maxX: TUNNEL_RAMP.maxX, minZ: TUNNEL_RAMP.minZ, maxZ: TUNNEL_FLOOR.maxZ };
    const subwayHole = { minX: SUBWAY_STAIR.minX, maxX: SUBWAY_STAIR.maxX, minZ: SUBWAY_LANDING.minZ, maxZ: SUBWAY_STAIR.maxZ };

    for (const hole of [tunnelHole, subwayHole]) {
      const hMinX = Math.max(hole.minX, minX);
      const hMaxX = Math.min(hole.maxX, maxX);
      const hMinZ = Math.max(hole.minZ, minZ);
      const hMaxZ = Math.min(hole.maxZ, maxZ);
      if (hMinX >= hMaxX || hMinZ >= hMaxZ) continue; // no overlap with this mesh's own bounds
      const path = new THREE.Path();
      path.moveTo(hMinX, -hMinZ);
      path.lineTo(hMaxX, -hMinZ);
      path.lineTo(hMaxX, -hMaxZ);
      path.lineTo(hMinX, -hMaxZ);
      path.lineTo(hMinX, -hMinZ);
      shape.holes.push(path);
    }

    return new THREE.ShapeGeometry(shape);
  }

  private addBuilding(b: { minX: number; maxX: number; minZ: number; maxZ: number; height: number; color: number }): THREE.Mesh {
    const w = b.maxX - b.minX;
    const d = b.maxZ - b.minZ;
    const cx = (b.minX + b.maxX) / 2;
    const cz = (b.minZ + b.maxZ) / 2;
    return this.addBox(cx, b.height / 2, cz, w, b.height, d, b.color, false);
  }

  private addBox(
    cx: number,
    cy: number,
    cz: number,
    w: number,
    h: number,
    d: number,
    color: number,
    isGround: boolean,
  ): THREE.Mesh {
    const geo = new THREE.BoxGeometry(w, h, d);
    const mat = new THREE.MeshStandardMaterial({ color, roughness: 0.9, metalness: 0 });
    const mesh = new THREE.Mesh(geo, mat);
    mesh.position.set(cx, cy, cz);
    this.group.add(mesh);
    if (isGround) this.groundMeshes.push(mesh);
    return mesh;
  }

  /**
   * L3 (PLAN-CITY3 §0, Wave P-2): a zero-thickness `PlaneGeometry` is nearly
   * invisible edge-on — "a floating sheet of paper," CITY3-FINDINGS.md's own
   * words for exactly this on ROOFTOP_D_RAMP, the single biggest
   * "unfinished" tell in the outdoor block. Every permanent gray ground
   * primitive is now a real ≥0.15m-thick box instead. `BoxGeometry(w,d,
   * GROUND_THICKNESS)` occupies the SAME local frame a flat `PlaneGeometry(w,
   * d)` did (a flat sheet at local z=0, XY = width/depth) — translating it by
   * `-GROUND_THICKNESS/2` before this mesh's own `rotation.x=-Math.PI/2` is
   * applied leaves the box's local-z=0 face (now its TOP after rotation)
   * exactly where the old plane's surface was, with the added thickness
   * extending along local -Z, i.e. straight down once rotated — top face at
   * exactly `r.y`, thickness hanging below it, never centered on it (which
   * would silently raise the walkable-reading surface by THICKNESS/2 above
   * the real collision height). `side: THREE.DoubleSide` is no longer needed
   * — a box has real backfaces.
   */
  private addFlatMesh(r: { minX: number; maxX: number; minZ: number; maxZ: number; y: number }, color: number, isGround: boolean): THREE.Mesh {
    const w = r.maxX - r.minX;
    const d = r.maxZ - r.minZ;
    const geo = new THREE.BoxGeometry(w, d, GROUND_THICKNESS);
    geo.translate(0, 0, -GROUND_THICKNESS / 2);
    const mat = new THREE.MeshStandardMaterial({ color, roughness: 0.9 });
    const mesh = new THREE.Mesh(geo, mat);
    mesh.rotation.x = -Math.PI / 2;
    mesh.position.set((r.minX + r.maxX) / 2, r.y, (r.minZ + r.maxZ) / 2);
    this.group.add(mesh);
    if (isGround) this.groundMeshes.push(mesh);
    return mesh;
  }

  /**
   * A ramp mesh's TOP face is a single tilted surface spanning its full run
   * — the exact shape `collision.ts`'s RampFloor already models, so the
   * visual and the walkable surface can never drift apart (one set of
   * numbers, not two). L3 (Wave P-2): same box-not-plane treatment as
   * `addFlatMesh` above, using the identical local-frame trick — the box's
   * un-rotated local z=0 face is where the flat plane used to be, so after
   * the SAME position/rotation this method already used, the top face
   * traces y0->y1 exactly (verified algebraically: at local y=-length/2 the
   * old rotation put the surface at world Y = r.y0, at +length/2 at r.y1 —
   * unchanged by adding a thin box behind that same z=0 face) and the added
   * thickness hangs perpendicular-below the slope, not straight down —
   * correct for a ramp, where "below the walkable surface" means below the
   * slope, not below in world-Y.
   */
  private addRampMesh(r: { minX: number; maxX: number; minZ: number; maxZ: number; y0: number; y1: number }, axis: 'x' | 'z', color: number): THREE.Mesh {
    const w = r.maxX - r.minX;
    const d = r.maxZ - r.minZ;
    const run = axis === 'z' ? d : w;
    const rise = r.y1 - r.y0;
    const length = Math.hypot(run, rise);
    const geo = new THREE.BoxGeometry(axis === 'z' ? w : length, axis === 'z' ? length : d, GROUND_THICKNESS);
    geo.translate(0, 0, -GROUND_THICKNESS / 2);
    const mat = new THREE.MeshStandardMaterial({ color, roughness: 0.95 });
    const mesh = new THREE.Mesh(geo, mat);
    const tilt = Math.atan2(rise, run);
    if (axis === 'z') {
      mesh.rotation.x = -Math.PI / 2 + tilt;
    } else {
      mesh.rotation.z = Math.PI / 2 - tilt;
      mesh.rotation.x = -Math.PI / 2;
    }
    mesh.position.set((r.minX + r.maxX) / 2, (r.y0 + r.y1) / 2, (r.minZ + r.maxZ) / 2);
    this.group.add(mesh);
    this.groundMeshes.push(mesh);
    return mesh;
  }

  private addTunnelWalls(): THREE.Mesh[] {
    const created: THREE.Mesh[] = [];
    const t = 0.4;
    const tf = TUNNEL_FLOOR;
    const wallMat = new THREE.MeshStandardMaterial({ color: 0x2c2d33, roughness: 0.95 });
    // T1 3b: west/east walls' TOP now matches their own collider's topY
    // (1.5, well above street level 0) instead of the tunnel's own low
    // ceiling (tf.y + TUNNEL_HEIGHT = -0.4 — BELOW street, not just flush
    // with it) — the mesh used to stop short of daylight entirely, so the
    // "opening" at the tunnel mouth read as a glitch (solid-looking street
    // with nothing marking where it's actually walkable) rather than a real
    // opening. bottomY stays at the floor (tf.y), unchanged. The BACK wall
    // keeps its original TUNNEL_HEIGHT-only visual — it's deep inside the
    // tunnel's dead end, never approached from street level, and out of
    // this brief's explicit scope ("raise the tunnel SIDE walls' visuals");
    // its collider (topY 40) intentionally stays taller than its visual for
    // an unrelated fast-fall-prevention reason (see allWalls()'s own
    // comment on it) that predates this fix.
    const sideTopY = 1.5; // matches allWalls()'s tunnel side-wall collider topY exactly — §0.4, visuals and colliders must never disagree
    const sideHeight = sideTopY - tf.y;
    // Found while verifying this fix (not something the brief flagged): the
    // PRE-EXISTING version of this segment tuple used `TUNNEL_RAMP.minZ`
    // (26, the range's START) as the box's CENTER z, with `d` as its full
    // depth — a BoxGeometry centers on its own position, so that actually
    // rendered z∈[26-d/2, 26+d/2] = [19,33], not the documented/intended
    // z∈[26,40] the matching collider in allWalls() actually uses (which is
    // written as explicit minZ/maxZ, not this center+depth shape, and was
    // already correct). A real visual/collider mismatch — confirmed by
    // reading the live mesh's world bounding box, not just re-deriving it on
    // paper — fixed here since this exact function was already being edited
    // for T1 3b's "raise the visuals" ask.
    const sideMinZ = TUNNEL_RAMP.minZ;
    const sideMaxZ = tf.maxZ;
    const sideCz = (sideMinZ + sideMaxZ) / 2;
    const sideDepth = sideMaxZ - sideMinZ;
    const sideSegments: Array<[number, number]> = [
      [tf.minX - t / 2, sideCz], // west wall
      [tf.maxX + t / 2, sideCz], // east wall
    ];
    for (const [cx, cz] of sideSegments) {
      const geo = new THREE.BoxGeometry(t, sideHeight, sideDepth);
      const mesh = new THREE.Mesh(geo, wallMat);
      mesh.position.set(cx, tf.y + sideHeight / 2, cz);
      this.group.add(mesh);
      created.push(mesh);
    }

    const backGeo = new THREE.BoxGeometry(tf.maxX - tf.minX + t * 2, TUNNEL_HEIGHT, t);
    const backMesh = new THREE.Mesh(backGeo, wallMat);
    backMesh.position.set((tf.minX + tf.maxX) / 2, tf.y + TUNNEL_HEIGHT / 2, tf.maxZ + t / 2);
    this.group.add(backMesh);
    created.push(backMesh);
    return created;
  }

  /** Visual match for layout.ts's SUBWAY_STAIR/SUBWAY_LANDING side-wall colliders. */
  private addSubwayWalls(): void {
    const t = 0.4;
    const topY = 1.5; // matches allWalls()'s subway side-wall collider topY exactly (T1 3b — previously the mesh topped out flush at y=0, well short of its own collider)
    const bottomY = SUBWAY_LANDING.y - 1; // matches the collider's own bottomY
    const height = topY - bottomY;
    const wallMat = new THREE.MeshStandardMaterial({ color: 0x2c2d33, roughness: 0.95 });
    const cz = (SUBWAY_LANDING.minZ + SUBWAY_STAIR.maxZ) / 2;
    const d = SUBWAY_STAIR.maxZ - SUBWAY_LANDING.minZ;
    const geo = new THREE.BoxGeometry(t, height, d);

    // East wall: mesh centered exactly on its own collider, as before.
    const eastMesh = new THREE.Mesh(geo, wallMat);
    eastMesh.position.set(SUBWAY_STAIR.maxX + t / 2, bottomY + height / 2, cz);
    this.group.add(eastMesh);

    // West wall: two prior versions were both wrong, in opposite directions
    // (T1 review, twice). (1) Centered on its collider (x[-8.4,-8]) the mesh
    // sits inside building_c's footprint with its +X face exactly coplanar
    // with the building's own face at x=-8 — invisible plus a z-fighting
    // stripe. (2) Shifted 0.4m east INTO the pit (x[-8,-7.6]) it detached
    // from its collider entirely: resolveHorizontal stops a westward player
    // at center x=-7.7 (collider edge -8.0 + radius 0.3), so the capsule
    // (x[-8,-7.4]) fully CONTAINED the visible mesh — a wall rendering
    // through the player's own body, the same §0.4 visual/collider
    // disagreement one step removed. The fix keeps the mesh ON its collider
    // footprint but shaves the +X face 2cm west (x[-8.4,-8.02]): no overlap
    // with the capsule's reachable range (min x=-8.0), no coplanar face
    // (2cm gap to building_c's x=-8), and the sliver north of the building
    // (z -8..-6) still renders as a visible wall. The 2cm visual recess
    // behind the collider surface is imperceptible.
    const westGeo = new THREE.BoxGeometry(t - 0.02, height, d);
    const westMesh = new THREE.Mesh(westGeo, wallMat);
    westMesh.position.set(SUBWAY_STAIR.minX - t / 2 - 0.01, bottomY + height / 2, cz);
    this.group.add(westMesh);
  }

  /**
   * Review fix: the T1 brief's own text called for a 0.4m curb here, but its
   * matching collider (layout.ts's `allWalls()`, same footprint) has to sit
   * at topY 1.5 to actually stop a jump into the pit (a 0.4m collider is
   * both jump-clearable AND too tall a rise to auto-vault at once) — the
   * same 1.5 the two flanking side-wall colliders already use. A 0.4m MESH
   * backed by a 1.1m-taller invisible collider is exactly the "visuals and
   * colliders disagree" bug §0.4 exists to prevent — `addTunnelWalls` and
   * `addSubwayWalls` above raise THEIR meshes to match their own colliders
   * for the identical reason. Raised to 1.5 here too — matching the
   * collider AND the flanking side walls exactly, so the rim reads as one
   * continuous parapet instead of a knee-high curb with an unexplained
   * invisible wall standing above it.
   */
  private addSubwayRimCurb(): THREE.Mesh {
    // Spans the collider's FULL vertical range (bottomY = SUBWAY_LANDING.y-1
    // = -4.5 up to topY 1.5), not just the street-level half — a mesh
    // covering only y[0,1.5] left an open horizontal slot (y[-1.5,0], the
    // gap between the gate's top and street level) showing raw scene
    // background when standing on the landing looking south (T1 review).
    const topY = 1.5;
    const bottomY = SUBWAY_LANDING.y - 1;
    const height = topY - bottomY;
    const cx = (SUBWAY_LANDING.minX + SUBWAY_LANDING.maxX) / 2;
    const w = SUBWAY_LANDING.maxX - SUBWAY_LANDING.minX;
    const d = 0.4;
    const cz = SUBWAY_LANDING.minZ - d / 2;
    return this.addBox(cx, bottomY + height / 2, cz, w, height, d, 0x55565f, false);
  }

  /**
   * T1 3d: a stepped gray-box handrail along the fire-escape ramp's open
   * east edge, built from the EXACT SAME collider data `allWalls()` uses
   * (`FIRE_ESCAPE_RAIL_SEGMENTS`, plus the `FIRE_ESCAPE_LANDING_RAIL`
   * extension onto ROOFTOP_LANDING's east edge — see that const's own
   * comment in layout.ts) — one set of numbers, not two, so the visible
   * rail and the walkable/blocked space can't drift apart (same discipline
   * as `addRampMesh`'s own comment).
   */
  private addFireEscapeRail(): THREE.Mesh[] {
    const created: THREE.Mesh[] = [];
    const railMat = new THREE.MeshStandardMaterial({ color: 0x9a9ea6, roughness: 0.8, metalness: 0.1 });
    for (const seg of [...FIRE_ESCAPE_RAIL_SEGMENTS, FIRE_ESCAPE_LANDING_RAIL]) {
      const w = seg.maxX - seg.minX;
      const d = seg.maxZ - seg.minZ;
      const h = seg.topY - seg.bottomY;
      const geo = new THREE.BoxGeometry(w, h, d);
      const mesh = new THREE.Mesh(geo, railMat);
      mesh.position.set((seg.minX + seg.maxX) / 2, seg.bottomY + h / 2, (seg.minZ + seg.maxZ) / 2);
      this.group.add(mesh);
      created.push(mesh);
    }
    return created;
  }

  /** B-1: the second ramp's drift-guard rail (world/layout.ts's ROOFTOP_D_RAIL_SEGMENTS/ROOFTOP_D_LANDING_RAIL) — same "one set of numbers, not two" construction as `addFireEscapeRail()` above, kept as its own method rather than a shared parameterized one so this doesn't risk touching the already-shipped, already-3-lens-reviewed fire-escape rail. */
  private addRooftopDRail(): THREE.Mesh[] {
    const created: THREE.Mesh[] = [];
    const railMat = new THREE.MeshStandardMaterial({ color: 0x9a9ea6, roughness: 0.8, metalness: 0.1 });
    for (const seg of [...ROOFTOP_D_RAIL_SEGMENTS, ROOFTOP_D_LANDING_RAIL]) {
      const w = seg.maxX - seg.minX;
      const d = seg.maxZ - seg.minZ;
      const h = seg.topY - seg.bottomY;
      const geo = new THREE.BoxGeometry(w, h, d);
      const mesh = new THREE.Mesh(geo, railMat);
      mesh.position.set((seg.minX + seg.maxX) / 2, seg.bottomY + h / 2, (seg.minZ + seg.maxZ) / 2);
      this.group.add(mesh);
      created.push(mesh);
    }
    return created;
  }

  /** A `BoxCollider`'s center/dimensions fed straight into `addBox` — the SAME numbers physics resolves against, so this can never visually disagree with what the collider actually occupies. */
  private addBoxCollider(c: BoxCollider, color: number, isGround = false): THREE.Mesh {
    return this.addBox((c.minX + c.maxX) / 2, (c.bottomY + c.topY) / 2, (c.minZ + c.maxZ) / 2, c.maxX - c.minX, c.topY - c.bottomY, c.maxZ - c.minZ, color, isGround);
  }

  /** S05: the play-area boundary ring (layout.ts's `boundaryWalls()`) — a distinct muted blue-gray, deliberately unlike any building/prop color in the block, so it reads as "the edge of what's built" rather than another wall to walk around. */
  private addBoundaryWalls(): void {
    for (const wall of boundaryWalls()) this.addBoxCollider(wall, 0x545b66);
  }

  /**
   * S05: the bodega interior (world/interior.ts) — floor, four walls (with
   * the doorway gap `interiorWalls()` already leaves), and a counter prop.
   * Reached only by teleport (world/interact.ts's `enter_bodega`/
   * `exit_bodega` actions, wired in main.ts) since `INTERIOR_ORIGIN` sits
   * far outside the exterior block — no ramp/stair connects the two, by
   * design (interior.ts's own header comment).
   */
  private addInterior(): THREE.Mesh[] {
    const created: THREE.Mesh[] = [];
    created.push(this.addFlatMesh({ minX: INTERIOR_ROOM.minX, maxX: INTERIOR_ROOM.maxX, minZ: INTERIOR_ROOM.minZ, maxZ: INTERIOR_ROOM.maxZ, y: 0 }, 0x4a4038, true));
    for (const wall of interiorWalls()) created.push(this.addBoxCollider(wall, 0x8a6f52));
    created.push(this.addBoxCollider(interiorCounterCollider(), INTERIOR_COUNTER.color));
    return created;
  }

  /** T3-1: a visible gray-box patch matching `INTERIOR_STOOP_FLOOR` (interior.ts) exactly — same color as the room's own floor so it reads as a continuous surface, not a separate feature. `isGround=true` (same as the room floor) so the dev-tap teleport and camera-occlusion raycast both see it. */
  private addInteriorStoop(): THREE.Mesh {
    return this.addFlatMesh({ minX: INTERIOR_STOOP_FLOOR.minX, maxX: INTERIOR_STOOP_FLOOR.maxX, minZ: INTERIOR_STOOP_FLOOR.minZ, maxZ: INTERIOR_STOOP_FLOOR.maxZ, y: INTERIOR_STOOP_FLOOR.y }, 0x4a4038, true);
  }

  /**
   * B-2 "Enterable interiors" (PLAN-CITY2 §3) — the laundromat: floor, four
   * walls (with `laundromatWalls()`'s own doorway gap), the machine bank,
   * and the walk-out stoop — mirrors `addInterior()`/`addInteriorStoop()`'s
   * exact structure for a second room. `isGround=true` on both flat meshes
   * (floor + stoop), same reasoning as the bodega's own (dev-tap teleport +
   * camera-occlusion raycast must both see it).
   */
  private addLaundromat(): THREE.Mesh[] {
    const created: THREE.Mesh[] = [];
    const floor = this.addFlatMesh({ minX: LAUNDROMAT_ROOM.minX, maxX: LAUNDROMAT_ROOM.maxX, minZ: LAUNDROMAT_ROOM.minZ, maxZ: LAUNDROMAT_ROOM.maxZ, y: 0 }, 0x3d4a52, true);
    floor.name = 'laundromat_floor_gray'; // Wave P-2: lets a harness check find it directly (getObjectByName ignores visibility, unlike raycastGroundDownAt's own effectively-visible filter — this room is hidden until entered)
    created.push(floor);
    for (const wall of laundromatWalls()) created.push(this.addBoxCollider(wall, 0x8a6f52));
    created.push(this.addBoxCollider(laundromatMachinesCollider(), LAUNDROMAT_MACHINES.color));
    created.push(
      this.addFlatMesh(
        { minX: LAUNDROMAT_STOOP_FLOOR.minX, maxX: LAUNDROMAT_STOOP_FLOOR.maxX, minZ: LAUNDROMAT_STOOP_FLOOR.minZ, maxZ: LAUNDROMAT_STOOP_FLOOR.maxZ, y: LAUNDROMAT_STOOP_FLOOR.y },
        0x3d4a52,
        true,
      ),
    );
    return created;
  }

  /**
   * B-2: the basement vestibule — floor + four FULLY SOLID walls (no doorway
   * gap, see interiorBasement.ts's own header comment) plus a small placard
   * on the north wall marking the sealed door (`BASEMENT_SEALED_DOOR_MARKER`)
   * — purely visual, no collider of its own (the wall behind it already
   * blocks), same "sign in front of a solid wall" idiom `addCoronaExitSign()`
   * already uses for `CORONA_EXIT_GATE`.
   */
  private addBasement(): THREE.Mesh[] {
    const created: THREE.Mesh[] = [];
    const floor = this.addFlatMesh({ minX: BASEMENT_ROOM.minX, maxX: BASEMENT_ROOM.maxX, minZ: BASEMENT_ROOM.minZ, maxZ: BASEMENT_ROOM.maxZ, y: 0 }, 0x2e2b28, true);
    floor.name = 'basement_floor_gray'; // Wave P-2: same reasoning as addLaundromat's own floor.name above
    created.push(floor);
    for (const wall of basementWalls()) created.push(this.addBoxCollider(wall, 0x4a453f));
    created.push(this.addBox(BASEMENT_SEALED_DOOR_MARKER.x, 1.1, BASEMENT_SEALED_DOOR_MARKER.z, 1.2, 2.0, 0.1, 0x5a2f2a, false));
    return created;
  }

  /**
   * B-2: a flush grate marking `BASEMENT_HATCH` on the tunnel floor —
   * interact-only (world/interact.ts's `enter_basement`), so unlike every
   * OTHER prop in this file it deliberately has NO collider of its own:
   * `TUNNEL_FLOOR`'s own floor region already covers this XZ, and a thin,
   * flush (0.05m) box is a walk-OVER marker, not an obstacle — same
   * reasoning `addHiddenStashCrate()`'s own comment gives for skipping a
   * collider on an interact-marker prop. Always visible (lives in the
   * exterior tunnel, reached before ever entering the basement — not part of
   * either new interiorGroup).
   */
  private addBasementHatchGrate(): void {
    this.addBox(BASEMENT_HATCH.x, TUNNEL_FLOOR.y + 0.025, BASEMENT_HATCH.z, 1.4, 0.05, 1.4, 0x24262c, false);
  }

  // ------------------------------------------------------------------
  // S09: real C04 Astoria block art, swapped in over the S01-S08 gray
  // boxes above. `physics` (built at the very top of the constructor) is
  // NEVER touched below — every method here only mutates `group`/
  // `groundMeshes`/`gray`, per ASSET-CONTRACT's own "these assets are
  // visual-only" law for this package. World placements are C04's own
  // measured numbers (report.json's `targetWorldPlacement`/`placementY`,
  // NOTES.md's per-piece notes) — not re-derived here, so a placement bug
  // is a data-entry error against a known-correct source, not a fresh
  // geometry computation to get wrong twice.
  // ------------------------------------------------------------------

  private async attachArt(): Promise<void> {
    // Independent, not chained — one slow/failed piece (network hiccup, a
    // malformed file) never delays or blocks any of the others; each
    // `attachX` only ever swaps ITS OWN gray key(s) once ITS OWN load
    // resolves. `allSettled` (not `all`) so a single rejection can't cancel
    // the rest — logged, not thrown, matching main.ts's existing tolerance
    // for a boss/enemy GLB that fails to load.
    const results = await Promise.allSettled([
      this.attachBuildingFacades(),
      this.attachGroundKit(),
      this.attachFireEscape(),
      this.attachRooftopDFireEscape(), // Wave P-2 (PLAN-CITY3 §3)
      this.attachTunnelKit(),
      this.attachSubwayEntrance(),
      this.attachParkProps(),
      this.attachInterior(),
      this.attachHellGate(),
      this.attachStreetProps(),
      this.attachConstructionPocket(),
      // B-3 (PLAN-CITY2 §3, Astoria detail pass, C06)
      this.attachElevatedLine(),
      // Wave E (PLAN-CITY3 §6, "THE STATION")
      this.attachElTrainStation(),
      this.attachElStationPlatform(),
      this.attachAstoriaParkCorner(),
      this.attachBeerGarden(),
      this.attachRestaurantRow(),
      this.attachStreetFair(),
      this.attachRfkVista(),
      this.attachTheBomb(),
      // Wave D (PLAN-CITY2 §4, C07 den kit)
      this.attachDenLair(),
      this.attachDenSewer(),
      this.attachGridlockDressing(),
    ]);
    for (const r of results) {
      if (r.status === 'rejected') console.error('[Block.attachArt] a C04 art piece failed to load — its gray-box fallback stays visible', r.reason);
    }
  }

  private readonly FACADE_PLACEMENT: Record<string, { file: string; pos: [number, number, number] }> = {
    building_a: { file: 'blk_facade_bodega.glb', pos: [11.5, 0, 17] },
    building_b: { file: 'blk_facade_greek_market.glb', pos: [22.5, 0, 17] },
    building_c: { file: 'blk_facade_auto_shop.glb', pos: [-17, 0, -17] },
    building_d: { file: 'blk_facade_walkup.glb', pos: [17, 0, -17] },
  };

  /**
   * The 4 facades (modules 1-4) — each also carries its own roof cap.
   * `building_a`'s `roof_deck` was the only one ever wired up (the original
   * S09 brief called the other 3 non-walkable) — B-1 (PLAN-CITY2 §3) found,
   * via a live world-space bbox probe, that EVERY facade actually ships its
   * own real `roof_deck`+`parapet_*` nodes at exactly that building's own
   * height/footprint (confirmed for building_b: y=14.00 across x[19,26]
   * z[8,26]; building_d: y=12.00 across x[8,26] z[-26,-8] — both match
   * BUILDINGS' own recorded numbers exactly), so B-1's rooftop network wires
   * building_b's and building_d's up too (layout.ts's ROOFTOP_B/ROOFTOP_D).
   * building_c's stays unregistered — no traversal reaches it yet. The
   * facade's own storefront geometry carries the bodega door read
   * (module 1), so `bodegaDoor`'s gray panel is removed alongside it too,
   * not separately.
   *
   * Review fix: each building's own load/failure is caught and handled
   * INSIDE the map callback (never lets one rejection propagate out of
   * `Promise.all`) — a first draft let any single facade's failure reject
   * the whole batch, which left every OTHER already-successfully-loaded
   * sibling's `this.group.add()` call still landing (its own async
   * callback wasn't cancelled) while the shared gray-removal line below
   * never ran — real art AND the gray box both rendering, permanently,
   * for every building that DIDN'T fail. Each building now removes its
   * OWN gray key the instant its OWN load succeeds.
   */
  private async attachBuildingFacades(): Promise<void> {
    await Promise.all(
      BUILDINGS.map(async (b) => {
        const spec = this.FACADE_PLACEMENT[b.id];
        if (!spec) return;
        try {
          const glb = await loadGLB(`/assets/models/${spec.file}`);
          const inst = instantiate(glb);
          inst.group.position.set(...spec.pos);
          this.group.add(inst.group);
          if (b.id === 'building_a') {
            const roofDeck = inst.group.getObjectByName('roof_deck');
            if (roofDeck) this.groundMeshes.push(roofDeck);
            this.removeGray('bodegaDoor', 'rooftop');
          }
          // B-1: building_b/building_d's own real roof_deck nodes — see this
          // method's own header comment for how this was confirmed live, and
          // this class's own constructor comment for why each gets a tracked
          // gray-box fallback (`rooftopB`/`rooftopD`) mirroring building_a's
          // own `rooftop` key, removed only on THIS building's own success.
          if (b.id === 'building_b') {
            const roofDeck = inst.group.getObjectByName('roof_deck');
            if (roofDeck) this.groundMeshes.push(roofDeck);
            this.removeGray('rooftopB');
            // Wave P-4 (PLAN-CITY3 §3): the real modeled `closed_glass_door`
            // supersedes the old blue placeholder slab entirely — see
            // LAUNDROMAT_DOOR's own comment for the exact measured x/width
            // this facade's door actually occupies.
            this.removeGray('laundromatDoor');
            // `produce_crate_0`/`_1` (world x[19.84,20.46]/[20.64,21.26],
            // measured live) sit exactly where the repositioned door's own
            // physical opening now is (x[20.22,22.12] at the new width 1.9)
            // — `produce_crate_1` lands ENTIRELY inside it, not just a
            // sliver, so a small nudge can't clear it without either
            // crowding `produce_crate_0` past the building's own west wall
            // (x=19) or crowding into `produce_crate_2`'s own space to the
            // east. Hidden rather than half-fixed — `produce_crate_2`/`_3`
            // (x[23.34,24.76]) are untouched and stay well clear of the new
            // door on the building's other side.
            for (const name of ['produce_crate_0', 'produce_crate_1']) {
              const crate = inst.group.getObjectByName(name);
              if (crate) crate.visible = false;
            }
          }
          if (b.id === 'building_d') {
            const roofDeck = inst.group.getObjectByName('roof_deck');
            if (roofDeck) this.groundMeshes.push(roofDeck);
            this.removeGray('rooftopD');
          }
          this.removeGray(`building_${b.id}`);
        } catch (err) {
          console.error(`[Block.attachBuildingFacades] ${b.id}'s facade failed to load — its gray-box fallback stays visible`, err);
        }
      }),
    );
  }

  /**
   * Module 13 — 4×4m tiles (`tile_street`/`tile_sidewalk`/`tile_crosswalk`/
   * `tile_park`) rendered via `THREE.InstancedMesh` (one draw call per tile
   * TYPE regardless of instance count — this project has no prior
   * InstancedMesh precedent, but tiling the full ~80×80m street/sidewalk/
   * park footprint one mesh per tile would be hundreds of draw calls for
   * zero visual benefit). Zone priority: crosswalk (the intersection
   * square) > street (either cross arm) > park (the open NW quadrant) >
   * sidewalk (the catch-all — also what tiles everywhere else, including
   * under the buildings, where it's invisible anyway).
   *
   * Review fix — a real visual bug, not just a comment inaccuracy: a WHOLE
   * tile is skipped the instant its 4×4 footprint overlaps either pit hole
   * AT ALL, which — since the hole rects aren't aligned to the 4m tile
   * grid — rounds each hole's exclusion zone outward to the next tile
   * boundary (measured: up to 2-3m of extra excluded ground past the
   * subway pit's real edge, right at its street-level mouth, the single
   * most-trafficked spot on the block). Tightening the tile grid can't fix
   * this at ANY granularity without either re-opening the opposite bug
   * (a tile rendering solid ground OVER part of an actual hole — a real
   * §0.4 visual/collision disagreement, strictly worse than an oversized
   * gap) or fragmenting the ground kit's own delivered tile geometry into
   * bespoke per-edge shapes it was never authored for. Fixed differently:
   * the gray-box street/sidewalk mesh (`buildHoledGroundShape`, this same
   * class, still exactly hole-accurate — three review rounds already
   * proved its polygon-hole math correct) is kept in the scene instead of
   * removed, and recolored to the tile kit's own materials below — the
   * InstancedMesh tiles add texture/variety everywhere they safely fit;
   * the old precise mesh, now tile-colored instead of flat gray, shows
   * through only in the thin collar right at each hole's true edge, so
   * the void this review found is closed exactly, not just narrowed.
   */
  /** A named node in this delivery is sometimes a single Mesh, sometimes a Group wrapping several (confirmed live: `tile_crosswalk` is a Group of 2 meshes — a base tile plus a separate emissive stripe mesh) — resolve either shape to the real mesh(es) inside, never assume a bare `instanceof Mesh` on the node itself. */
  private resolveMeshes(node: THREE.Object3D | undefined): THREE.Mesh[] {
    if (!node) return [];
    if (node instanceof THREE.Mesh) return [node];
    const meshes: THREE.Mesh[] = [];
    node.traverse((child) => {
      if (child instanceof THREE.Mesh) meshes.push(child);
    });
    return meshes;
  }

  private async attachGroundKit(): Promise<void> {
    const glb = await loadGLB('/assets/models/blk_ground_kit.glb');
    const inst = instantiate(glb);
    type TileKind = 'street' | 'sidewalk' | 'crosswalk' | 'park';
    const tileSource: Record<TileKind, THREE.Mesh[]> = {
      street: this.resolveMeshes(inst.group.getObjectByName('tile_street')),
      sidewalk: this.resolveMeshes(inst.group.getObjectByName('tile_sidewalk')),
      crosswalk: this.resolveMeshes(inst.group.getObjectByName('tile_crosswalk')),
      park: this.resolveMeshes(inst.group.getObjectByName('tile_park')),
    };
    if ((Object.keys(tileSource) as TileKind[]).some((k) => tileSource[k].length === 0)) {
      throw new Error('blk_ground_kit.glb is missing an expected named tile mesh');
    }

    // C13A bakes the correct palette in its textured atlas. The optional
    // A/B meshes have identical footprints and seamless edge treatment.
    const variants: Partial<Record<'street' | 'sidewalk' | 'park', THREE.Mesh[]>> = {
      street: this.resolveMeshes(inst.group.getObjectByName('tile_street_b')),
      sidewalk: this.resolveMeshes(inst.group.getObjectByName('tile_sidewalk_b')),
      park: this.resolveMeshes(inst.group.getObjectByName('tile_park_b')),
    };
    const setGroundAnisotropy = (mesh: THREE.Mesh): void => {
      const materials = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
      for (const material of materials) {
        if (material instanceof THREE.MeshStandardMaterial && material.map) {
          material.map.anisotropy = 8;
          material.map.needsUpdate = true;
          // The crosswalk stripe's emissiveMap is a SEPARATE THREE.Texture
          // sharing the same atlas image — without its own anisotropy it
          // mip-blurs differently from the albedo at grazing angles.
          if (material.emissiveMap) {
            material.emissiveMap.anisotropy = 8;
            material.emissiveMap.needsUpdate = true;
          }
        }
      }
    };
    Object.values(tileSource).flat().forEach(setGroundAnisotropy);
    Object.values(variants).flat().forEach(setGroundAnisotropy);

    const TILE = 4;
    const HALF = 40;
    const placements: Record<TileKind, Array<[number, number]>> = { street: [], sidewalk: [], crosswalk: [], park: [] };

    const tunnelHole = { minX: TUNNEL_RAMP.minX, maxX: TUNNEL_RAMP.maxX, minZ: TUNNEL_RAMP.minZ, maxZ: TUNNEL_FLOOR.maxZ };
    const subwayHole = { minX: SUBWAY_STAIR.minX, maxX: SUBWAY_STAIR.maxX, minZ: SUBWAY_LANDING.minZ, maxZ: SUBWAY_STAIR.maxZ };
    const overlapsHole = (x: number, z: number): boolean => {
      const x0 = x - TILE / 2, x1 = x + TILE / 2, z0 = z - TILE / 2, z1 = z + TILE / 2;
      for (const h of [tunnelHole, subwayHole]) {
        if (x1 > h.minX && x0 < h.maxX && z1 > h.minZ && z0 < h.maxZ) return true;
      }
      return false;
    };

    for (let x = -HALF + TILE / 2; x < HALF; x += TILE) {
      for (let z = -HALF + TILE / 2; z < HALF; z += TILE) {
        if (overlapsHole(x, z)) continue;
        const inCrossX = Math.abs(x) <= STREET_HALF_WIDTH;
        const inCrossZ = Math.abs(z) <= STREET_HALF_WIDTH;
        let kind: TileKind;
        if (inCrossX && inCrossZ) kind = 'crosswalk';
        else if (inCrossX || inCrossZ) kind = 'street';
        else if (x < -STREET_HALF_WIDTH && z > STREET_HALF_WIDTH) kind = 'park';
        else kind = 'sidewalk';
        placements[kind].push([x, z]);
      }
    }

    const m = new THREE.Matrix4();
    for (const kind of Object.keys(placements) as TileKind[]) {
      const coords = placements[kind];
      if (coords.length === 0) continue;
      // Usually one mesh per tile type; `crosswalk` is two (base + stripe) —
      // both get their own InstancedMesh sharing the SAME coordinate list,
      // so the stripe overlay tracks the base tile at every instance.
      const alternate = (kind === 'street' || kind === 'sidewalk' || kind === 'park') ? variants[kind] : undefined;
      const useAlternates = alternate && alternate.length === tileSource[kind].length;
      for (let meshIndex = 0; meshIndex < tileSource[kind].length; meshIndex++) {
        const source = tileSource[kind][meshIndex];
        const alternateSource = useAlternates ? alternate[meshIndex] : undefined;
        const primaryCoords: Array<[number, number]> = [];
        const alternateCoords: Array<[number, number]> = [];
        coords.forEach(([x, z]) => {
          const hash = ((Math.floor((x + HALF) / TILE) * 17 + Math.floor((z + HALF) / TILE) * 31) >>> 0) % 5;
          (alternateSource && hash === 0 ? alternateCoords : primaryCoords).push([x, z]);
        });
        const addInstances = (mesh: THREE.Mesh, instances: Array<[number, number]>, suffix: string): void => {
          if (instances.length === 0) return;
          const im = new THREE.InstancedMesh(mesh.geometry, mesh.material, instances.length);
          im.name = `tile_${kind}${suffix}_instanced`;
          instances.forEach(([x, z], i) => {
            m.makeTranslation(x, 0, z);
            im.setMatrixAt(i, m);
          });
          im.instanceMatrix.needsUpdate = true;
          this.group.add(im);
          this.groundMeshes.push(im);
        };
        const primarySuffix = kind === 'crosswalk' ? (meshIndex === 0 ? '_base' : '_stripe') : '';
        addInstances(source, primaryCoords, primarySuffix);
        if (alternateSource) addInstances(alternateSource, alternateCoords, '_b');
      }
    }

    // Recolor-and-keep, not remove: see this method's own header comment.
    // The collars are tinted with HAND-MEASURED texture-era anchors — the
    // post-gutter inner-field averages of the C13A atlas (street #505255,
    // sidewalk #A59885). They cannot sample COLOR_0 (textured tiles
    // deliberately omit it), and unlike the retired vertex-color-averaging
    // approach these hexes do NOT self-update: if the ground atlas is ever
    // repainted (e.g. a C13 revision), re-measure both anchors by hand or
    // the collars silently drift from the tiles around them. The harness's
    // "C13A atlas palette anchors" check samples the live atlas against
    // these same values and will catch a drift.
    const streetGray = this.gray.street;
    const sidewalkGray = this.gray.sidewalk;
    if (streetGray instanceof THREE.Mesh) this.tintFlat(streetGray, '#505255');
    if (sidewalkGray instanceof THREE.Mesh) this.tintFlat(sidewalkGray, '#A59885');
    delete this.gray.street;
    delete this.gray.sidewalk;
  }

  /** Colors the narrow hole collar with C13A's measured texture-era anchor. */
  private tintFlat(target: THREE.Mesh, hex: string): void {
    const materials = Array.isArray(target.material) ? target.material : [target.material];
    for (const material of materials) {
      if (material instanceof THREE.MeshStandardMaterial) {
        material.vertexColors = false;
        material.map = null;
        material.color.set(hex);
      }
    }
  }

  /**
   * Module 5 — `flight_grate` is the one 4m-rise/4m-run repeatable unit
   * (NOTES.md); 4 copies at `(0,4,4)` steps from the ramp's own start
   * exactly cover FIRE_ESCAPE_RAMP's real 16m/16m run. Every OTHER named
   * node in the file (posts/toprails/tread bars/brackets) travels along
   * with each flight copy — only the landing-specific nodes are hidden per
   * copy (`.visible=false`, not removed — sidesteps any assumption about
   * how deep the glTF node hierarchy nests, since visibility alone still
   * excludes a node from both rendering AND `Raycaster` by default). The
   * landing itself is placed once, separately, at its own recorded target;
   * everything else in THAT copy is hidden instead, using the identical
   * technique in reverse.
   */
  private async attachFireEscape(): Promise<void> {
    const glb = await loadGLB('/assets/models/blk_fire_escape.glb');
    const LANDING_NODES = ['top_landing', 'landing_toprail', 'landing_post_0', 'landing_post_1', 'landing_post_2', 'landing_post_3', 'landing_post_4'];

    for (let i = 0; i < 4; i++) {
      const inst = instantiate(glb);
      for (const name of LANDING_NODES) {
        const n = inst.group.getObjectByName(name);
        if (n) n.visible = false;
      }
      inst.group.position.set(16, i * 4, 10 + i * 4);
      this.group.add(inst.group);
      const grate = inst.group.getObjectByName('flight_grate');
      if (grate) this.groundMeshes.push(grate);
    }

    const landingInst = instantiate(glb);
    landingInst.group.traverse((obj) => {
      if (obj !== landingInst.group && obj.name && !LANDING_NODES.includes(obj.name)) obj.visible = false;
    });
    landingInst.group.position.set(16, 12, 22);
    this.group.add(landingInst.group);
    const landingDeck = landingInst.group.getObjectByName('top_landing');
    if (landingDeck) this.groundMeshes.push(landingDeck);

    this.removeGray('fireEscapeRamp', 'fireEscapeRail', 'rooftopLanding');
  }

  /**
   * Wave P-2 (PLAN-CITY3 §3, CITY3-FINDINGS.md's own "CHEAP" fix): reskins
   * ROOFTOP_D_RAMP + ROOFTOP_D_LANDING with the SAME `blk_fire_escape.glb`
   * asset and the exact same tiling idiom `attachFireEscape()` above uses —
   * `flight_grate` is a 4m-rise/4m-run repeatable unit, and ROOFTOP_D_RAMP's
   * run (maxZ-minZ=12) and rise (y1-y0=12) are each exactly 3 of those
   * units, no remainder. The landing instance reuses `attachFireEscape()`'s
   * own trick: positioned at the LAST flight's own coordinate (not one step
   * further), showing only the landing-specific nodes instead of the
   * stepped ones — that flight's own local geometry already carries its own
   * top landing at local +4/+4, which is why the main fire escape's landing
   * (i=3 at z=22,y=12) ends up exactly at the ramp's real top (z=26,y=16)
   * with zero extra math; the same relationship holds here (i=2 at
   * z=-16,y=8 -> deck lands at z=-12,y=12, matching ROOFTOP_D_RAMP's own
   * maxZ/y1 exactly).
   */
  private async attachRooftopDFireEscape(): Promise<void> {
    const glb = await loadGLB('/assets/models/blk_fire_escape.glb');
    const LANDING_NODES = ['top_landing', 'landing_toprail', 'landing_post_0', 'landing_post_1', 'landing_post_2', 'landing_post_3', 'landing_post_4'];
    const cx = (ROOFTOP_D_RAMP.minX + ROOFTOP_D_RAMP.maxX) / 2;
    const flightCount = (ROOFTOP_D_RAMP.maxZ - ROOFTOP_D_RAMP.minZ) / 4;

    for (let i = 0; i < flightCount; i++) {
      const flight = instantiate(glb);
      for (const name of LANDING_NODES) {
        const n = flight.group.getObjectByName(name);
        if (n) n.visible = false;
      }
      flight.group.position.set(cx, i * 4, ROOFTOP_D_RAMP.minZ + i * 4);
      this.group.add(flight.group);
      const grate = flight.group.getObjectByName('flight_grate');
      if (grate) this.groundMeshes.push(grate);
    }

    const landingInst = instantiate(glb);
    landingInst.group.traverse((obj) => {
      if (obj !== landingInst.group && obj.name && !LANDING_NODES.includes(obj.name)) obj.visible = false;
    });
    landingInst.group.position.set(cx, (flightCount - 1) * 4, ROOFTOP_D_RAMP.minZ + (flightCount - 1) * 4);
    this.group.add(landingInst.group);
    const landingDeck = landingInst.group.getObjectByName('top_landing');
    if (landingDeck) this.groundMeshes.push(landingDeck);

    this.removeGray('rooftopDRamp', 'rooftopDRail', 'rooftopDLanding');
  }

  /**
   * Module 8 — report.json lists TWO targets ("ramp" 17,-3,27.5 and
   * "corridor" 17,-3,30.375), 2.875m apart in Z — NOT the same anchor
   * documented twice. Confirmed live (not assumed): placing the whole file
   * at the corridor target alone lands the corridor tiles exactly right
   * (z29-40, matching TUNNEL_FLOOR) but `ramp_piece` ~2.9m off TUNNEL_RAMP's
   * real z26-29. `ramp_piece`'s own local position, measured the same way,
   * turned out to already be centered on ITS OWN local origin (0,0,0) —
   * so detaching it and placing it independently at the ramp's own target
   * is correct, not a guess: re-measured after the fix, it lands exactly
   * on z26-29/y0-to-3. Ceilings stay hidden per the brief's own instruction
   * (no vertical/overhead collision exists yet).
   */
  private async attachTunnelKit(): Promise<void> {
    const glb = await loadGLB('/assets/models/blk_tunnel_kit.glb');
    const inst = instantiate(glb);

    const ramp = inst.group.getObjectByName('ramp_piece');
    if (ramp) {
      inst.group.remove(ramp);
      ramp.position.set(17, -3, 27.5);
      this.group.add(ramp);
      this.groundMeshes.push(ramp);
    }

    inst.group.position.set(17, -3, 30.375);
    for (const name of ['tile_1_ceiling', 'tile_2_ceiling', 'tile_3_ceiling', 'tile_4_ceiling']) {
      const n = inst.group.getObjectByName(name);
      if (n) n.visible = false;
    }
    this.group.add(inst.group);

    for (const name of ['tile_1_floor', 'tile_2_floor', 'tile_3_floor', 'tile_4_floor']) {
      const n = inst.group.getObjectByName(name);
      if (n) this.groundMeshes.push(n);
    }

    this.removeGray('tunnelRamp', 'tunnelFloor', 'tunnelWalls');
  }

  /** Module 7 — one system placement (report.json's `targetWorldPlacement`); its own `railpost_*` ring visually takes over the standalone rim curb's job, so that gray box is removed here too, not separately. `addSubwayWalls()`'s side walls have no matching art in this package and stay gray (flagged in STATUS-CITY, not this project's scope to invent geometry for). */
  private async attachSubwayEntrance(): Promise<void> {
    const glb = await loadGLB('/assets/models/blk_subway_entrance.glb');
    const inst = instantiate(glb);
    inst.group.position.set(-5.5, -3.5, -12);
    this.group.add(inst.group);

    const landing = inst.group.getObjectByName('landing');
    if (landing) this.groundMeshes.push(landing);
    for (let i = 1; i <= 12; i++) {
      const tread = inst.group.getObjectByName(`stair_tread_${String(i).padStart(2, '0')}`);
      if (tread) this.groundMeshes.push(tread);
    }

    this.removeGray('subwayStair', 'subwayLanding', 'subwayGate', 'subwayRimCurb');
  }

  /**
   * Modules 10 — `prp_planter`×2 + `prp_bench`×2 at `PARK_PROPS`' own
   * positions (footprint-center/min-Y=0 pivots, per the format contract —
   * a direct `(p.x,0,p.z)` placement needs no extra offset). Each loaded
   * group is pushed to `groundMeshes` directly (not a specific named
   * sub-mesh) — `raycastGround`'s `recursive:true` finds whichever real
   * surface inside actually gets hit.
   *
   * Review fix: same per-item try/catch + per-item gray key as
   * `attachBuildingFacades` — a first draft gated all 4 props' gray-box
   * removal on one shared `Promise.all` batch, so one failed load would
   * have stranded the other 3 (already-succeeded) props double-rendered.
   */
  private async attachParkProps(): Promise<void> {
    const specs: Array<{ id: string; file: string }> = [
      { id: 'planter_1', file: 'prp_planter.glb' },
      { id: 'planter_2', file: 'prp_planter.glb' },
      { id: 'bench_1', file: 'prp_bench.glb' },
      { id: 'bench_2', file: 'prp_bench.glb' },
    ];
    await Promise.all(
      specs.map(async (spec) => {
        const p = PARK_PROPS.find((pp) => pp.id === spec.id);
        if (!p) return;
        try {
          const glb = await loadGLB(`/assets/models/${spec.file}`);
          const inst = instantiate(glb);
          inst.group.position.set(p.x, 0, p.z);
          this.group.add(inst.group);
          this.groundMeshes.push(inst.group);
          this.removeGray(`park_${spec.id}`);
        } catch (err) {
          console.error(`[Block.attachParkProps] ${spec.id} failed to load — its gray-box fallback stays visible`, err);
        }
      }),
    );
  }

  /** Module 12 — a complete room replacement (floor/4 walls incl. the entry-gap/counter/shelves), placed at the SAME world anchor `interior.ts`'s own `INTERIOR_ROOM`/`interiorWalls()` are centered on, so the visual and the (unchanged) collision agree by construction. Ceiling hidden, same reasoning as the tunnel's. */
  private async attachInterior(): Promise<void> {
    const glb = await loadGLB('/assets/models/blk_bodega_interior.glb');
    const inst = instantiate(glb);
    inst.group.position.set(INTERIOR_ORIGIN.x, 0, INTERIOR_ORIGIN.z);
    const ceiling = inst.group.getObjectByName('ceiling');
    if (ceiling) ceiling.visible = false;
    // T3-1: `interiorGroup`, not `this.group` directly — this real art must
    // inherit the SAME visibility toggle as the gray-box room it replaces
    // (`setInteriorVisible()`), or the interior would render outdoors again
    // the instant this async load resolves, regardless of `insideBodega`.
    this.interiorGroup.add(inst.group);

    const floor = inst.group.getObjectByName('floor');
    if (floor) this.groundMeshes.push(floor);
    const counter = inst.group.getObjectByName('counter');
    if (counter) {
      // Confirmed live (not assumed): the delivered counter MESH's own
      // vertex data carries a baked-in offset (its object `.position` is
      // identity — the offset lives in the geometry itself, unlike a
      // node authored at its own footprint-center pivot) that doesn't
      // match interiorCounterCollider()'s real world center. Measuring the
      // ACTUAL current world position first and nudging by the delta needed
      // (not assigning an absolute target to `.position`) is what makes
      // this correct regardless of where that baked-in offset came from —
      // an earlier version of this fix assumed `.position` alone WAS the
      // offset and assigned it directly, which silently stacked a second
      // offset on top of the geometry's existing one (measured landing 1.9m
      // further off than before the "fix").
      counter.updateWorldMatrix(true, false);
      const before = new THREE.Box3().setFromObject(counter).getCenter(new THREE.Vector3());
      const c = interiorCounterCollider();
      counter.position.x += (c.minX + c.maxX) / 2 - before.x;
      counter.position.z += (c.minZ + c.maxZ) / 2 - before.z;
      this.groundMeshes.push(counter); // the vaultable top the gray counter box already was
    }

    this.removeGray('interior');
  }

  /**
   * Module 9 — pure atmosphere, zero collision, zero gray-box equivalent
   * (nothing to remove). "Final position/scale is S09's call" per the
   * brief: placed north of the boundary ring, scaled up to read as a
   * distant, monumental river crossing rather than a small nearby prop —
   * an easy follow-up tune, not load-bearing on anything.
   */
  private async attachHellGate(): Promise<void> {
    const glb = await loadGLB('/assets/models/blk_hellgate_backdrop.glb');
    const inst = instantiate(glb);
    inst.group.position.set(0, 14, 110);
    inst.group.scale.setScalar(5);
    this.group.add(inst.group);
  }

  /**
   * Module 11 — pure street dressing, zero collision (no gray-box
   * equivalent ever existed for these; PLAN-CITY's own gray-box law never
   * called for placeholder lamps/signals/trash cans). A first-pass,
   * deliberately modest placement (life on the block, not a full dressing
   * pass) — easy to add to or reposition in a later polish session.
   */
  private async attachStreetProps(): Promise<void> {
    const specs: Array<{ file: string; pos: [number, number, number]; rotationY?: number }> = [
      { file: 'prp_lamp.glb', pos: [-6.8, 0, -30] },
      { file: 'prp_lamp.glb', pos: [6.8, 0, 30] },
      { file: 'prp_signal.glb', pos: [5, 0, 5] },
      { file: 'prp_trash_can.glb', pos: [9, 0, 6] },
      { file: 'prp_trash_can.glb', pos: [-15, 0, 10] },
      { file: 'prp_taxi.glb', pos: [6.8, 0, -20], rotationY: Math.PI / 2 },
      // T3-3 review fix (MEDIUM, correctness lens): was [-15,0,18] — its real
      // measured collider (layout.ts's STREET_PROP_COLLIDERS) overlapped
      // ai/habitats.ts's park_airspace zone closely enough to trap the
      // dive_pigeon habitat slot's own real (seeded-RNG) home point
      // (-13.833,19.437) against the collider's edge. Shifted straight
      // south by 4m — live-verified clear of every other prop/collider at
      // its new position (see STATUS-CITY.md's T3 review-fix log).
      { file: 'prp_food_cart.glb', pos: [-15, 0, 14] },
    ];
    await Promise.all(
      specs.map(async (spec) => {
        const glb = await loadGLB(`/assets/models/${spec.file}`);
        const inst = instantiate(glb);
        inst.group.position.set(...spec.pos);
        if (spec.rotationY) inst.group.rotation.y = spec.rotationY;
        this.group.add(inst.group);
      }),
    );
  }

  /**
   * S10: `blk_scaffold` perimeter dressing for the construction pocket
   * (layout.ts's `CONSTRUCTION_SCAFFOLDS`/`CONSTRUCTION_POCKET`) — pure
   * atmosphere, zero collision, no gray-box equivalent (this zone never had
   * ANY geometry before S10, real or gray — same "first real content here"
   * shape as `attachHellGate`/`attachStreetProps` above). One shared source
   * GLB, instantiated once per position per `instantiate()`'s own per-clone
   * material-independence contract (core/assets.ts) — matters here even
   * though nothing tints these, since a future pass adding per-instance
   * variation (weathering, a warning-light tint) would need it anyway.
   */
  private async attachConstructionPocket(): Promise<void> {
    const glb = await loadGLB('/assets/models/blk_scaffold.glb');
    for (const spec of CONSTRUCTION_SCAFFOLDS) {
      const inst = instantiate(glb);
      inst.group.position.set(spec.x, 0, spec.z);
      inst.group.rotation.y = spec.rotationY;
      this.group.add(inst.group);
    }
  }

  // ------------------------------------------------------------------
  // B-3 (PLAN-CITY2 §3, Astoria detail pass) — C06 art, all NEW content
  // (no gray-box phase, no S09-era placeholder to remove — same "first real
  // content here" shape as attachHellGate/attachStreetProps/
  // attachConstructionPocket above). `physics` is untouched by every method
  // below; collision for this batch was wired into the constructor's own
  // BlockPhysics array literals straight from layout.ts's EL_*/POOLHOUSE_*/
  // etc. exports, not re-derived here.
  // ------------------------------------------------------------------

  /**
   * Piers (rotationY=π/2 so their columns straddle the street's Z-width —
   * layout.ts's own EL_PIER_COLLIDERS comment has the full rotation
   * derivation) + one continuous 6-tile deck run (unrotated — local X, the
   * 8m tiling axis, already runs along world X, so it needs no rotation to
   * align with the east-west street) + the station stair. Deck tile centers
   * are the midpoints between consecutive piers, derived here rather than a
   * separate layout.ts export since EL_LINE_PIER_XS already fixes them.
   */
  private async attachElevatedLine(): Promise<void> {
    const [pierGlb, deckGlb, stairGlb] = await Promise.all([
      loadGLB('/assets/models/blk_el_pier.glb'),
      loadGLB('/assets/models/blk_el_deck.glb'),
      loadGLB('/assets/models/blk_el_station_stair.glb'),
    ]);
    for (const x of EL_LINE_PIER_XS) {
      const inst = instantiate(pierGlb);
      inst.group.position.set(x, 0, 0);
      inst.group.rotation.y = Math.PI / 2;
      this.group.add(inst.group);
    }
    for (let i = 0; i < EL_LINE_PIER_XS.length - 1; i++) {
      const tileX = (EL_LINE_PIER_XS[i] + EL_LINE_PIER_XS[i + 1]) / 2;
      const inst = instantiate(deckGlb);
      inst.group.position.set(tileX, EL_DECK_PLACEMENT_Y, 0);
      this.group.add(inst.group);
      this.groundMeshes.push(inst.group);
    }
    // Live-verified (world-space bbox probe, not the "street stair foot"
    // pivot label taken at face value): the delivered stair's local bbox is
    // symmetric around its own origin (Z -4.5..4.5), so its pivot is
    // actually at the geometric CENTER, not the street-level foot the
    // report.json label implies. Placing at maxZ (the street end) shipped
    // the visual 4.5m north of EL_STATION_STAIR's own collision ramp
    // (rendered Z 6.5-15.5 against a collision Z 2-11) — centering fixes
    // the footprint exactly.
    //
    // REVIEW FIX (CRITICAL, correctness lens): centering the footprint alone
    // still left the stair's SLOPE DIRECTION backwards. Measured live: the
    // lowest tread (`stair_tread`) sits at local Z -4.5..-3.6 (Y 0-0.74) and
    // the highest (`stair_tread.009`) at local Z +3.6..+4.5 (Y 6.66-7.4) —
    // i.e. the model's own foot is at −Z, its top at +Z. Unrotated at this
    // position, that puts the FOOT at world z=2 (the deck edge) and the TOP
    // at world z=11 (the sidewalk) — exactly backwards from
    // `EL_STATION_STAIR`'s own ramp (y0=EL_DECK_WALK_Y at minZ=2, y1=0 at
    // maxZ=11 — i.e. the COLLISION ramp is high at z=2, low at z=11).
    // rotationY=π flips both axes around the (symmetric) local origin,
    // landing the model's own foot at world z=11 (street) and its top at
    // world z=2 (deck) — matching the collision ramp's real slope. The
    // footprint stays X 26.5-29.5 / Z 2-11 either way, since both axes are
    // symmetric about the origin.
    const stairInst = instantiate(stairGlb);
    stairInst.group.position.set((EL_STATION_STAIR.minX + EL_STATION_STAIR.maxX) / 2, 0, (EL_STATION_STAIR.minZ + EL_STATION_STAIR.maxZ) / 2);
    stairInst.group.rotation.y = Math.PI;
    this.group.add(stairInst.group);
    this.groundMeshes.push(stairInst.group);
  }

  // ------------------------------------------------------------------
  // Wave E (PLAN-CITY3 §6, "THE STATION"): C11's train/track-strip/bumper.
  // Collision for all three is wired into the constructor's own
  // BlockPhysics array literals (EL_TRAIN_COLLIDER/EL_TRAIN_NOSE_COLLIDER/
  // EL_BUMPER_COLLIDER/EL_DECK_EAST_END_CAP, straight from layout.ts's own
  // measured exports), not re-derived here — same "physics wired in the
  // constructor, visuals attached async" split as every other B-3/Wave-D
  // batch above.
  // ------------------------------------------------------------------

  /**
   * All three static (0 animation clips). The train's roof
   * (`roof_rib_*`/`roof_vent_*`/vent nodes) is deliberately NEVER pushed to
   * `groundMeshes` — nothing in this game climbs onto a parked train's
   * roof, and doing so would need a real FloorRegion this wave doesn't
   * build (§13's own "every roof_deck node has a FloorRegion or an
   * explicit non-walkable listing" standing check, satisfied here by
   * omission + the harness/run.mjs check this wave adds). The track-tie
   * strip is ALSO never pushed to groundMeshes (zero collision by design,
   * per C11's NOTES.md) — `raycastGroundDownAt` must keep reading
   * EL_DECK_WALK_Y off the real deck mesh underneath, not off a rail-tie
   * tile sitting at the identical height.
   */
  private async attachElTrainStation(): Promise<void> {
    const [trainGlb, stripGlb, bumperGlb] = await Promise.all([
      loadGLB('/assets/models/blk_el_train_car.glb'),
      loadGLB('/assets/models/blk_el_track_strip.glb'),
      loadGLB('/assets/models/blk_el_bumper.glb'),
    ]);
    const train = instantiate(trainGlb);
    train.group.position.set(EL_TRAIN_POS.x, EL_TRAIN_PLACEMENT_Y, EL_TRAIN_POS.z);
    this.group.add(train.group);

    for (const x of EL_TRACK_STRIP_XS) {
      const strip = instantiate(stripGlb);
      strip.group.position.set(x, EL_TRACK_STRIP_PLACEMENT_Y, 0);
      this.group.add(strip.group);
    }

    // Unrotated: the bumper's own local -X (hazard stripes/red lamp) faces
    // world -X (west) — a 180° flip would show the blank stencil side
    // instead, per C11's own NOTES.md.
    const bumper = instantiate(bumperGlb);
    bumper.group.position.set(EL_BUMPER_POS.x, EL_BUMPER_PLACEMENT_Y, EL_BUMPER_POS.z);
    this.group.add(bumper.group);
  }

  /**
   * E-3: platform content. Reuses the SAME shipped `prp_bench`/`prp_lamp`/
   * `prp_trash_can` GLBs already attached elsewhere on the block (loadGLB
   * caches by URL, core/assets.ts) — no new asset. All 5 instances are
   * unrotated, footprint-center pivots, matching every other reuse of these
   * props in this file.
   */
  private async attachElStationPlatform(): Promise<void> {
    const [benchGlb, lampGlb, trashCanGlb] = await Promise.all([
      loadGLB('/assets/models/prp_bench.glb'),
      loadGLB('/assets/models/prp_lamp.glb'),
      loadGLB('/assets/models/prp_trash_can.glb'),
    ]);
    for (const p of EL_STATION_BENCH_POS) {
      const inst = instantiate(benchGlb);
      inst.group.position.set(p.x, EL_DECK_WALK_Y, p.z);
      this.group.add(inst.group);
    }
    for (const p of EL_STATION_LAMP_POS) {
      const inst = instantiate(lampGlb);
      inst.group.position.set(p.x, EL_DECK_WALK_Y, p.z);
      this.group.add(inst.group);
    }
    const trashCan = instantiate(trashCanGlb);
    trashCan.group.position.set(EL_STATION_TRASH_CAN_POS.x, EL_DECK_WALK_Y, EL_STATION_TRASH_CAN_POS.z);
    this.group.add(trashCan.group);
  }

  /** Astoria Park corner (brief module C) — poolhouse entry facade + lifeguard chair + flagpole. All unrotated, footprint-center pivots. */
  private async attachAstoriaParkCorner(): Promise<void> {
    const [poolhouseGlb, chairGlb, flagpoleGlb] = await Promise.all([
      loadGLB('/assets/models/blk_park_poolhouse.glb'),
      loadGLB('/assets/models/prp_lifeguard_chair.glb'),
      loadGLB('/assets/models/prp_park_flagpole.glb'),
    ]);
    const poolhouse = instantiate(poolhouseGlb);
    poolhouse.group.position.set(POOLHOUSE_POS.x, 0, POOLHOUSE_POS.z);
    this.group.add(poolhouse.group);
    const chair = instantiate(chairGlb);
    chair.group.position.set(LIFEGUARD_CHAIR_POS.x, 0, LIFEGUARD_CHAIR_POS.z);
    this.group.add(chair.group);
    const flagpole = instantiate(flagpoleGlb);
    flagpole.group.position.set(FLAGPOLE_POS.x, 0, FLAGPOLE_POS.z);
    this.group.add(flagpole.group);
  }

  /** Beer garden corner (brief module D) — walled corner (rotationY=π so the gate faces the block interior — layout.ts's own BEER_GARDEN_COLLIDERS comment has the rotation derivation) + longtable + string lights. */
  private async attachBeerGarden(): Promise<void> {
    const [wallGlb, tableGlb, lightsGlb] = await Promise.all([
      loadGLB('/assets/models/blk_beergarden_wall.glb'),
      loadGLB('/assets/models/prp_longtable.glb'),
      loadGLB('/assets/models/prp_string_lights.glb'),
    ]);
    const wall = instantiate(wallGlb);
    wall.group.position.set(BEER_GARDEN_POS.x, 0, BEER_GARDEN_POS.z);
    wall.group.rotation.y = Math.PI;
    this.group.add(wall.group);
    const table = instantiate(tableGlb);
    table.group.position.set(LONGTABLE_POS.x, 0, LONGTABLE_POS.z);
    this.group.add(table.group);
    const lights = instantiate(lightsGlb);
    lights.group.position.set(STRING_LIGHTS_POS.x, 0, STRING_LIGHTS_POS.z);
    this.group.add(lights.group);
  }

  /** Restaurant row (brief module E) — 2 standalone storefronts (rotationY=π/2, fronts facing west toward the sidewalk — every existing building's own C04 facade already wraps its full footprint, so there's no bare wall to mount these on; layout.ts's TAVERNA_POS/SEAFOOD_POS comment has the full reasoning) + fish crate/display dressing. */
  private async attachRestaurantRow(): Promise<void> {
    const [tavernaGlb, seafoodGlb, crateGlb, displayGlb] = await Promise.all([
      loadGLB('/assets/models/blk_storefront_taverna.glb'),
      loadGLB('/assets/models/blk_storefront_seafood.glb'),
      loadGLB('/assets/models/prp_fish_crate.glb'),
      loadGLB('/assets/models/prp_fish_display.glb'),
    ]);
    const taverna = instantiate(tavernaGlb);
    taverna.group.position.set(TAVERNA_POS.x, 0, TAVERNA_POS.z);
    taverna.group.rotation.y = Math.PI / 2;
    this.group.add(taverna.group);
    const seafood = instantiate(seafoodGlb);
    seafood.group.position.set(SEAFOOD_POS.x, 0, SEAFOOD_POS.z);
    seafood.group.rotation.y = Math.PI / 2;
    this.group.add(seafood.group);
    const crate = instantiate(crateGlb);
    crate.group.position.set(FISH_CRATE_POS.x, 0, FISH_CRATE_POS.z);
    this.group.add(crate.group);
    const display = instantiate(displayGlb);
    display.group.position.set(FISH_DISPLAY_POS.x, 0, FISH_DISPLAY_POS.z);
    this.group.add(display.group);
  }

  /** Street fair dressing (brief module F) — fair stall + grill cart, both unrotated near the park pocket. */
  private async attachStreetFair(): Promise<void> {
    const [stallGlb, cartGlb] = await Promise.all([loadGLB('/assets/models/prp_fair_stall.glb'), loadGLB('/assets/models/prp_grill_cart.glb')]);
    const stall = instantiate(stallGlb);
    stall.group.position.set(FAIR_STALL_POS.x, 0, FAIR_STALL_POS.z);
    this.group.add(stall.group);
    const cart = instantiate(cartGlb);
    cart.group.position.set(GRILL_CART_POS.x, 0, GRILL_CART_POS.z);
    this.group.add(cart.group);
  }

  /** Skyline vista (brief module A) — RFK bridge, pure atmosphere like attachHellGate above: zero collision, zero groundMeshes registration, well beyond the play boundary. Two towers 24m apart with one span tile between them. */
  private async attachRfkVista(): Promise<void> {
    const [towerGlb, spanGlb] = await Promise.all([loadGLB('/assets/models/blk_rfk_tower.glb'), loadGLB('/assets/models/blk_rfk_span.glb')]);
    for (const x of RFK_TOWER_XS) {
      const inst = instantiate(towerGlb);
      inst.group.position.set(x, RFK_TOWER_PLACEMENT_Y, RFK_VISTA_Z);
      this.group.add(inst.group);
    }
    const span = instantiate(spanGlb);
    span.group.position.set(RFK_SPAN_CENTER_X, RFK_SPAN_PLACEMENT_Y, RFK_VISTA_Z);
    this.group.add(span.group);
  }

  /**
   * THE BOMB (the brief's "hero pair") — replaces the bodega's gray
   * HEAL_STATION marker with the real pickup model. Purely visual:
   * HEAL_STATION's own interact logic (world/interact.ts) is untouched, so
   * this needs no gameplay wiring. Plus the hanging exterior sign above
   * BODEGA_DOOR.
   */
  private async attachTheBomb(): Promise<void> {
    const [bombGlb, signGlb] = await Promise.all([loadGLB('/assets/models/prp_the_bomb.glb'), loadGLB('/assets/models/prp_bomb_sign.glb')]);
    const bomb = instantiate(bombGlb);
    bomb.group.position.set(HEAL_STATION.x, 0, HEAL_STATION.z);
    this.interiorGroup.add(bomb.group);
    this.removeGray('healMarker');
    const sign = instantiate(signGlb);
    sign.group.position.set(BOMB_SIGN_POS.x, BOMB_SIGN_POS.y, BOMB_SIGN_POS.z);
    this.group.add(sign.group);
  }

  // ------------------------------------------------------------------
  // Wave D (PLAN-CITY2 §4, C07 den kit). Unlike `attachInterior()`/
  // `attachLaundromat`-equivalents (which swap real art over an EXISTING
  // S05-era gray box), the C07 kit ships real geometry for both den
  // interiors from the start — placed directly into `lairGroup`/
  // `sewerGroup`, both hidden by default (matching `interiorGroup`'s own
  // "only ever renders while the player is actually inside" idiom), no
  // gray-box predecessor to swap out. `lair_ceiling`/`chamber_ceiling`/
  // `pipe_crown` are hidden in code (per each GLB's own NOTES.md: "the
  // engine has no overhead collision yet") — collision itself is built
  // entirely from `world/denLair.ts`/`world/denSewer.ts`'s own data, never
  // read from a `COL_*` node (this delivery ships none — see this session's
  // own independent GLB inspection, matching this engine's established
  // "collision is authored in code" convention, world/layout.ts's own
  // header comment).
  // ------------------------------------------------------------------

  private async attachDenLair(): Promise<void> {
    const [roomGlb, pillarGlb, breachGlb] = await Promise.all([
      loadGLB('/assets/models/blk_basement_lair.glb'),
      loadGLB('/assets/models/prp_lair_pillar.glb'),
      loadGLB('/assets/models/blk_tunnel_breach.glb'),
    ]);
    const room = instantiate(roomGlb);
    room.group.position.set(LAIR_ORIGIN.x, 0, LAIR_ORIGIN.z);
    const ceiling = room.group.getObjectByName('lair_ceiling');
    if (ceiling) ceiling.visible = false;
    this.lairGroup.add(room.group);
    this.groundMeshes.push(room.group); // dev-tap teleport + camera-occlusion raycast, same convention every other real-art room registers with

    for (const p of LAIR_PILLARS) {
      const inst = instantiate(pillarGlb);
      inst.group.position.set(p.x, 0, p.z);
      this.lairGroup.add(inst.group);
      this.lairPillarInstances.push(inst.group);
    }

    // Visual only, per NOTES.md ("the panel itself needs zero collision") —
    // mounted on the tunnel's own real dead-end wall (world/layout.ts's
    // `allWalls()`, untouched by this session), NOT inside the isolated
    // `lairGroup` (the breach is part of the EXTERIOR tunnel, always visible
    // once the player has walked that far, unlike the isolated lair room).
    const breach = instantiate(breachGlb);
    breach.group.position.set(TUNNEL_BREACH_CENTER.x, TUNNEL_BREACH_PLACEMENT_Y, TUNNEL_BREACH_CENTER.z);
    this.group.add(breach.group);
  }

  private async attachDenSewer(): Promise<void> {
    const [chamberGlb, pipeGlb, trapGlb] = await Promise.all([
      loadGLB('/assets/models/blk_sewer_chamber.glb'),
      loadGLB('/assets/models/blk_sewer_pipe.glb'),
      loadGLB('/assets/models/prp_trap_hole.glb'),
    ]);
    const chamber = instantiate(chamberGlb);
    chamber.group.position.set(SEWER_ORIGIN.x, 0, SEWER_ORIGIN.z);
    const chamberCeiling = chamber.group.getObjectByName('chamber_ceiling');
    if (chamberCeiling) chamberCeiling.visible = false;
    // Wave P-3 (PLAN-CITY3 §3): the delivered `water_basin` node ships as a
    // plain concrete-palette mesh — swap in the procedural ripple/
    // transparency water material (waterMaterial.ts), reusable as-is for
    // Wave N's park pool later.
    const waterBasin = chamber.group.getObjectByName('water_basin');
    if (waterBasin instanceof THREE.Mesh) applyWaterMaterial(waterBasin);
    this.sewerGroup.add(chamber.group);
    this.groundMeshes.push(chamber.group);

    // 2 pipe segments (4.0m each), running from PIPE_ENTRY.z to the
    // chamber's own south mouth (SEWER_CHAMBER.minZ) — placementY:0 (flat
    // outer base) per the delivered GLB's own convention.
    const segCount = 2;
    const segLen = PIPE_RUN_LENGTH / segCount;
    for (let i = 0; i < segCount; i++) {
      const inst = instantiate(pipeGlb);
      const segZ = PIPE_ENTRY.z + segLen / 2 + i * segLen;
      inst.group.position.set(SEWER_ORIGIN.x, 0, segZ);
      const crown = inst.group.getObjectByName('pipe_crown');
      if (crown) crown.visible = false;
      this.sewerGroup.add(inst.group);
    }

    // The trap hole itself lives in the EXTERIOR block (a real street-level
    // feature, not part of the isolated sewer origin) — placementY -1.2 per
    // NOTES.md, same "position.y set directly to the delivered placementY"
    // convention as blk_subway_entrance/blk_tunnel_kit.
    const trap = instantiate(trapGlb);
    trap.group.position.set(TRAP_HOLE_POS.x, TRAP_HOLE_PLACEMENT_Y, TRAP_HOLE_POS.z);
    this.group.add(trap.group);
  }

  /** D-2: Gridlock arena wreck dressing + the barrier line closing the pocket's two approach mouths. All 3 wrecks are unrotated (long axis along X, matching each delivered GLB's own authoring — world/arena.ts's `GRIDLOCK_WRECKS` comment has the full placement derivation). */
  private async attachGridlockDressing(): Promise<void> {
    const [carGlb, vanGlb, stackGlb, barrierGlb] = await Promise.all([
      loadGLB('/assets/models/prp_car_wrecked.glb'),
      loadGLB('/assets/models/prp_van_wrecked.glb'),
      loadGLB('/assets/models/prp_car_stack.glb'),
      loadGLB('/assets/models/prp_barrier_fence.glb'),
    ]);
    const glbByFile: Record<string, LoadedGLB> = {
      'prp_car_wrecked.glb': carGlb,
      'prp_van_wrecked.glb': vanGlb,
      'prp_car_stack.glb': stackGlb,
    };
    for (const w of GRIDLOCK_WRECKS) {
      const inst = instantiate(glbByFile[w.file]);
      inst.group.position.set(w.x, 0, w.z);
      this.group.add(inst.group);
    }
    for (const s of [...GRIDLOCK_BARRIER_NORTH, ...GRIDLOCK_BARRIER_SOUTH]) {
      const inst = instantiate(barrierGlb);
      inst.group.position.set(s.x, 0, s.z);
      this.group.add(inst.group);
    }
    this.group.add(this.buildGridlockEntryMouth());
  }

  /**
   * K-1(d): "a built entry mouth per L5's walked-threshold rule (visible
   * mouth + crossing toast/sting)" — a real ≥0.15m hazard-stripe threshold
   * box (L3: never a naked plane) dressing the barrier gap at
   * `GRIDLOCK_ENTRY_MOUTH`. Plain procedural geometry, not a C07/C12 GLB —
   * no asset exists for this yet; a real prop can replace it later without
   * touching anything that reads `GRIDLOCK_ENTRY_MOUTH` itself. No
   * collider: it's floor-level dressing the player walks OVER, not a
   * physical obstacle (the barrier fence segments already own the actual
   * blocking geometry).
   */
  private buildGridlockEntryMouth(): THREE.Mesh {
    const width = GRIDLOCK_ENTRY_MOUTH_HALF_WIDTH * 2;
    const geo = new THREE.BoxGeometry(width, 0.18, 0.5);
    const mat = new THREE.MeshStandardMaterial({ color: 0xffb020, roughness: 0.75, metalness: 0.1, emissive: 0x3a1e00, emissiveIntensity: 0.4 });
    const mesh = new THREE.Mesh(geo, mat);
    mesh.position.set(GRIDLOCK_ENTRY_MOUTH.x, 0.09, GRIDLOCK_ENTRY_MOUTH.z);
    return mesh;
  }
}
